/**
 * WMS Service
 *
 * Service layer for Works Management System API calls
 */

import axiosInstance from '@/services/interceptor';
import type {
  WMSFilters,
  WMSDashboardStats,
  WorkStatusDistribution,
  SchemeAllocation,
  ZonePerformance,
  UlbPerformance,
  WorkTypePerformance,
  StagePerformance,
  SchemePerformance,
  SchemeWiseDetails,
  SchemeDetailsGroupBy,
  DesignStatusSummary,
  ComponentSummary,
  ComponentDrilldownRow,
  MajorComponentsBySector,
  PermissionStatusSummary,
  PermissionDetailSummary,
  ProcurementStatusSummary,
  ProcurementWorkRow,
  PermissionWorkRow,
  ContractorPerformanceData,
  MonthlyProgressTrend,
  ProgressTrendPoint,
  WorkSummary,
  PipelineStage,
  WMSAlert,
  ULBMaster,
  ZoneMaster,
  WardMaster,
  SchemeMaster,
  WorkTypeMaster,
  FinancialYearMaster,
  TenderDetails
} from '../lib/types';

const BASE_URL = 'ds-wms';

// Response type wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Pipe-type master row (wms_pipe_type_lst_t) — drives the Procurement pipe-type dropdown.
export interface PipeTypeMaster {
  pipe_type_id: number;
  pipe_type_cd: string;
  pipe_type_nm: string;
}

export interface FundAllocationByUlbFy {
  udaId: number;
  udaName: string;
  districtName: string;
  fyId: number;
  fyCode: string;
  amount: number;
  utilized: number;
}

export interface UlbFundAllocationByScheme {
  schemeId: number;
  schemeName: string;
  fyId: number;
  fyCode: string;
  amount: number;
  utilized: number;
}

// Pushkaralu dashboard payload (GET /dashboard/pushkaralu)
export interface PushkaraluUlbRow {
  ulb: string;
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  physical: number;
  financial: number;
  budget: number;
  projectCost: number;
  sanctioned: number;
  expenditure: number;
  balance: number;
  valueOfWorkDone: number;
  tlOnTrack: number;
  tlDelayed: number;
  tlCritical: number;
  contractorCount: number;
  contractorOnTrack: number;
  contractorDelayed: number;
  contractorCritical: number;
}
export interface PushkaraluActivityRow {
  activityCd: string;
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  physical: number;
  financial: number;
}
export interface PushkaraluWorkRow {
  workId: number;
  workCode: string;
  workName: string;
  ulb: string;
  activityCd: string | null;
  amount: number;
  physical: number;
  financial: number;
  achievedPct: number;
  targetPct: number;
  hasSchedule: boolean;
  contractor: string;
  lat: number | null;
  lng: number | null;
  targetDate: string | null;
  nextMilestoneNm: string | null;
  nextMilestonePct: number | null;
  nextMilestoneDt: string | null;
  status: string;
}
// A single "Recent Updates" feed item — the latest progress upload reported from
// the field app for a work (UDA · work name · % completed · reported time).
export interface PushkaraluRecentUpdate {
  workId: number;
  workName: string;
  ulb: string;
  activityCd: string | null;
  physical: number;
  updatedTs: string | null;
}
// The 6 Pushkaralu host UDAs (from wms_admn_unt_lst_t where is_pushkaralu_in).
export interface PushkaraluHostUlb {
  id: number;
  code: string;
  name: string;
  lat: number | null;
  lng: number | null;
}
export interface PushkaraluDashboardData {
  kpis: {
    totalBudget: number;
    totalWorks: number;
    completed: number;
    completedPct: number;
    inProgress: number;
    inProgressPct: number;
    notStarted: number;
    notStartedPct: number;
    physicalProgress: number;
    financialProgress: number;
  };
  hostUlbs: PushkaraluHostUlb[];
  ulbs: PushkaraluUlbRow[];
  activities: PushkaraluActivityRow[];
  works: PushkaraluWorkRow[];
  recentUpdates: PushkaraluRecentUpdate[];
}

/** One payment in the FY: a WMS bill paid on a Finance voucher, or a Tally voucher. */
export interface FyMoneyRelease {
  source: string;
  refCd: string | null;
  voucherNo: string | null;
  paidDt: string | null;
  amount: number;
  wrkId: number;
  wrkCd: string | null;
  wrkNm: string | null;
  /** The work's own FY — older works paid in this year show an earlier one. */
  wrkFy: string | null;
  schemeId: number | null;
  schemeName: string | null;
  udaName: string | null;
}
// GET /dashboard/fy-money
export interface FyMoney {
  fyCode: string;
  from: string;
  to: string;
  releasesTotal: number;
  releases: FyMoneyRelease[];
  /** Works the other filters leave in, ignoring the FY. */
  workIds: number[];
}

/**
 * Dashboard API
 */
export const dashboardApi = {
  async getStats(filters?: WMSFilters): Promise<WMSDashboardStats> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<WMSDashboardStats>>(`${BASE_URL}/dashboard/stats`, { params });
    return response.data.data;
  },

  // Money paid in the selected FY on any work, as the Finance dashboard counts it;
  // null when no FY is selected.
  async getFyMoney(filters?: WMSFilters): Promise<FyMoney | null> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<FyMoney | null>>(`${BASE_URL}/dashboard/fy-money`, { params });
    return response.data.data ?? null;
  },

  // Pushkaralu (Godavari Pushkarams 2027) standalone dashboard — single payload
  // of KPIs, per-UDA rollup, per-activity rollup and recent tagged works.
  async getPushkaralu(): Promise<PushkaraluDashboardData> {
    const response = await axiosInstance.get<ApiResponse<PushkaraluDashboardData>>(`${BASE_URL}/dashboard/pushkaralu`);
    return response.data.data;
  },

  async getStatusDistribution(filters?: WMSFilters): Promise<WorkStatusDistribution[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<WorkStatusDistribution[]>>(`${BASE_URL}/dashboard/status-distribution`, { params });
    return response.data.data;
  },

  async getSchemeAllocation(filters?: WMSFilters): Promise<SchemeAllocation[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<SchemeAllocation[]>>(`${BASE_URL}/dashboard/scheme-allocation`, { params });
    return response.data.data;
  },

  async getFundAllocationByUlbFy(filters?: WMSFilters): Promise<FundAllocationByUlbFy[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<FundAllocationByUlbFy[]>>(`${BASE_URL}/dashboard/fund-allocation-by-ulb-fy`, { params });
    return response.data.data;
  },

  async getUlbFundAllocationByScheme(udaId: number, filters?: WMSFilters): Promise<UlbFundAllocationByScheme[]> {
    const params = { ...buildFilterParams(filters), udaId };
    const response = await axiosInstance.get<ApiResponse<UlbFundAllocationByScheme[]>>(`${BASE_URL}/dashboard/ulb-fund-allocation-by-scheme`, { params });
    return response.data.data;
  },

  async getZonePerformance(filters?: WMSFilters): Promise<ZonePerformance[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<ZonePerformance[]>>(`${BASE_URL}/dashboard/zone-performance`, { params });
    return response.data.data;
  },

  async getUlbPerformance(filters?: WMSFilters): Promise<UlbPerformance[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<UlbPerformance[]>>(`${BASE_URL}/dashboard/ulb-performance`, { params });
    return response.data.data;
  },

  async getMonthlyTrend(filters?: WMSFilters, months?: number): Promise<MonthlyProgressTrend[]> {
    const params = { ...buildFilterParams(filters), months };
    const response = await axiosInstance.get<ApiResponse<MonthlyProgressTrend[]>>(`${BASE_URL}/dashboard/monthly-trend`, { params });
    return response.data.data;
  },

  // Advanced physical + financial progress trend over real reporting periods,
  // bucketed monthly (default) or weekly. Powers the Analytics "Progress over time".
  async getProgressTrend(filters?: WMSFilters, granularity: 'monthly' | 'weekly' = 'monthly', months?: number): Promise<ProgressTrendPoint[]> {
    const params = { ...buildFilterParams(filters), granularity, months };
    const response = await axiosInstance.get<ApiResponse<ProgressTrendPoint[]>>(`${BASE_URL}/dashboard/progress-trend`, { params });
    return response.data.data;
  },

  // Per-WORK period progress trend — { [wrk_id]: { physical, financial } }, each
  // a [{label,target,achieved}] series. Powers the List view's monthly/weekly bars.
  async getWorksProgressTrend(granularity: 'monthly' | 'weekly' = 'monthly', months?: number): Promise<Record<string, { physical: Array<{ label: string; target: number; achieved: number }>; financial: Array<{ label: string; target: number; achieved: number }> }>> {
    const response = await axiosInstance.get<ApiResponse<any>>(`${BASE_URL}/dashboard/works-progress-trend`, { params: { granularity, months } });
    return response.data.data || {};
  },

  async getPipelineStages(filters?: WMSFilters): Promise<PipelineStage[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<PipelineStage[]>>(`${BASE_URL}/dashboard/pipeline-stages`, { params });
    return response.data.data;
  },

  async getAlerts(udaId?: string, limit?: number): Promise<WMSAlert[]> {
    const params = { udaId, limit };
    const response = await axiosInstance.get<ApiResponse<WMSAlert[]>>(`${BASE_URL}/dashboard/alerts`, { params });
    return response.data.data;
  },

  async getWorkTypePerformance(filters?: WMSFilters): Promise<WorkTypePerformance[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<WorkTypePerformance[]>>(`${BASE_URL}/dashboard/work-type-performance`, { params });
    return response.data.data;
  },

  async getStagePerformance(filters?: WMSFilters): Promise<StagePerformance[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<StagePerformance[]>>(`${BASE_URL}/dashboard/stage-performance`, { params });
    return response.data.data;
  },

  async getSchemePerformance(filters?: WMSFilters): Promise<SchemePerformance[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<SchemePerformance[]>>(`${BASE_URL}/dashboard/scheme-performance`, { params });
    return response.data.data;
  },

  async getContractorPerformance(filters?: WMSFilters): Promise<ContractorPerformanceData[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<ContractorPerformanceData[]>>(`${BASE_URL}/dashboard/contractor-performance`, { params });
    return response.data.data;
  },

  // Scheme-wise Details breakdown table (the row above the Row-1 cards). Grouped
  // by scheme (default) or by contractor / package / circle / district / ulb.
  // Returns dynamic Sector columns (Sector = work type) plus grounded /
  // not-grounded breakdown. The dashboard card toggles scheme ↔ contractor.
  async getSchemeWiseDetails(groupBy: SchemeDetailsGroupBy = 'scheme', filters?: WMSFilters): Promise<SchemeWiseDetails> {
    const params = { ...buildFilterParams(filters), groupBy };
    const response = await axiosInstance.get<ApiResponse<SchemeWiseDetails>>(`${BASE_URL}/dashboard/scheme-wise-details`, { params });
    return response.data.data;
  },

  // Scheme-wise Design Status (Hydraulic/Structural totals + approved) — "Design Status" toggle.
  async getDesignStatus(filters?: WMSFilters): Promise<DesignStatusSummary> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<DesignStatusSummary>>(`${BASE_URL}/dashboard/design-status`, { params });
    return response.data.data;
  },

  // Component count summary grouped by scheme / circle / ulb — "Major Components" toggle.
  async getComponentSummary(groupBy: 'scheme' | 'circle' | 'ulb' = 'scheme', filters?: WMSFilters): Promise<ComponentSummary> {
    const params = { ...buildFilterParams(filters), groupBy };
    const response = await axiosInstance.get<ApiResponse<ComponentSummary>>(`${BASE_URL}/dashboard/component-summary`, { params });
    return response.data.data;
  },

  // Components inside one group (click-through drill-down for Major Components).
  async getComponentDrilldown(groupBy: 'scheme' | 'circle' | 'ulb', groupId: number | string | null, filters?: WMSFilters): Promise<ComponentDrilldownRow[]> {
    const params: Record<string, any> = { ...buildFilterParams(filters), groupBy };
    if (groupId !== null && groupId !== undefined && groupId !== '') params.groupId = groupId;
    const response = await axiosInstance.get<ApiResponse<ComponentDrilldownRow[]>>(`${BASE_URL}/dashboard/component-drilldown`, { params });
    return response.data.data;
  },

  // Major Components — sector-wise pivot (sub-sector columns × scheme rows).
  async getMajorComponentsBySector(filters?: WMSFilters): Promise<MajorComponentsBySector> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<MajorComponentsBySector>>(`${BASE_URL}/dashboard/major-components`, { params });
    return response.data.data;
  },

  // Scheme-wise Permission / Site availability status — "Permission Status" toggle.
  async getPermissionStatus(filters?: WMSFilters): Promise<PermissionStatusSummary> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<PermissionStatusSummary>>(`${BASE_URL}/dashboard/permission-status`, { params });
    return response.data.data;
  },

  // Per-UDA site-availability / permissions for one scheme — drill-down shown on clicking a scheme.
  async getPermissionDetail(schemeId: number, filters?: WMSFilters): Promise<PermissionDetailSummary> {
    const params = buildFilterParams({ ...(filters || {}), schemeId: String(schemeId) } as WMSFilters);
    const response = await axiosInstance.get<ApiResponse<PermissionDetailSummary>>(`${BASE_URL}/dashboard/permission-detail`, { params });
    return response.data.data;
  },

  // Scheme-wise Procurement of Pipes (order value + ordered/dispatched/received) — "Procurement" toggle.
  // pipeTypeId (optional) scopes the roll-up to a single pipe type (Procurement view dropdown).
  async getProcurementStatus(filters?: WMSFilters, pipeTypeId?: number | string | null): Promise<ProcurementStatusSummary> {
    const params = { ...buildFilterParams(filters), ...(pipeTypeId ? { pipeTypeId } : {}) };
    const response = await axiosInstance.get<ApiResponse<ProcurementStatusSummary>>(`${BASE_URL}/dashboard/procurement-status`, { params });
    return response.data.data;
  },

  // Pipe-type master — feeds the pipe-type dropdown on the Procurement view.
  async getPipeTypes(): Promise<PipeTypeMaster[]> {
    const response = await axiosInstance.get<ApiResponse<PipeTypeMaster[]>>(`${BASE_URL}/masters/pipe-types`);
    return response.data.data || [];
  },

  // Per-work pipe procurement for one scheme — drill-down shown on clicking a scheme.
  async getProcurementWorks(schemeId: number, filters?: WMSFilters): Promise<{ rows: ProcurementWorkRow[] }> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<{ rows: ProcurementWorkRow[] }>>(`${BASE_URL}/dashboard/procurement-works/${schemeId}`, { params });
    return response.data.data;
  },

  // Per-work site & permission for one scheme — shown in the works table on scheme click.
  async getPermissionWorks(schemeId: number, filters?: WMSFilters): Promise<{ rows: PermissionWorkRow[] }> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<{ rows: PermissionWorkRow[] }>>(`${BASE_URL}/dashboard/permission-works/${schemeId}`, { params });
    return response.data.data;
  },

  async getContractorWorks(contractorId?: number, contractorName?: string): Promise<any[]> {
    const params: Record<string, any> = {};
    if (contractorName) params.contractorName = contractorName;
    else if (contractorId) params.contractorId = contractorId;
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/contractor-works`, { params });
    return response.data.data;
  },

  // Scheme-wise assigned-contractor timeline tracking (on-track / delayed / critical
  // per scheme+contractor). Powers the scheme -> assigned contractor timeline card.
  async getSchemeContractorTimeline(filters?: WMSFilters): Promise<any[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/scheme-contractor-timeline`, { params });
    return response.data.data;
  },

  // Payments card of the Stage-wise Work Status panel — DTCP bills by status.
  async getPaymentBillSummary(filters?: WMSFilters): Promise<{
    worksCount: number; billsCount: number; totalAmount: number;
    subStages: Array<{ code: string; name: string; billsCount: number; worksCount: number; amount: number }>;
  }> {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard/payment-bill-summary`, { params: buildFilterParams(filters) });
    return response.data.data;
  },

  /** Approved Budget Management amount behind the works' budget heads (one per head + UDA). */
  async getWorksBudget(filters?: WMSFilters): Promise<{
    totalApproved: number; worksCount: number;
    heads: Array<{ headId: number; headName: string; udaCode: string; worksCount: number; workCodes: string;
      financialYear: string | null; estimateType: string | null; approvedAmount: number }>;
  }> {
    const response = await axiosInstance.get(`${BASE_URL}/dashboard/works-budget`, { params: buildFilterParams(filters) });
    return response.data.data;
  },

  // Drill-down works list for a timeline bucket — powers the click-through pop-up.
  async getTimelineWorks(opts: { schemeId?: number | string; contractorId?: number | string; bucket?: string }, filters?: WMSFilters): Promise<any[]> {
    // Dashboard filters first; the clicked card's scheme/contractor then override them.
    const params: Record<string, any> = { ...buildFilterParams(filters) };
    if (opts.schemeId !== undefined && opts.schemeId !== null && opts.schemeId !== '') params.schemeId = opts.schemeId;
    if (opts.contractorId !== undefined && opts.contractorId !== null && opts.contractorId !== '') params.contractorId = opts.contractorId;
    if (opts.bucket) params.bucket = opts.bucket;
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/timeline-works`, { params });
    return response.data.data;
  },

  // Work-schedule monitoring summary (overall + current-date live metrics).
  async getScheduleMonitoring(filters?: WMSFilters): Promise<any> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<any>>(`${BASE_URL}/dashboard/schedule-monitoring`, { params });
    return response.data.data;
  },

  // Drill-down works list for a monitoring metric (click-through on the card).
  async getMonitoringDrilldown(metric: string): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/monitoring-drilldown`, { params: { metric } });
    return response.data.data;
  },

  // Latest project site photos for the dashboard gallery (optionally by scheme).
  // recentMonths = N returns EVERY photo uploaded within the last N months of
  // upload activity (relative to the most recent photo) — all schemes/packages,
  // no count cap.
  async getLatestProjectImages(opts?: { schemeId?: number | string; limit?: number; recentMonths?: number; pushkaralu?: boolean }): Promise<any[]> {
    const params: Record<string, any> = {};
    if (opts?.schemeId !== undefined && opts?.schemeId !== null && opts?.schemeId !== '') params.schemeId = opts.schemeId;
    if (opts?.limit) params.limit = opts.limit;
    if (opts?.recentMonths) params.recentMonths = opts.recentMonths;
    if (opts?.pushkaralu) params.pushkaralu = true;
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/latest-images`, { params });
    return response.data.data;
  },

  async getWorkTypeDistribution(filters?: WMSFilters): Promise<any[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/work-type-distribution`, { params });
    return response.data.data;
  },

  async getWorkCategoryDistribution(filters?: WMSFilters): Promise<any[]> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/work-category-distribution`, { params });
    return response.data.data;
  },

  async getTenderDetails(workId: number): Promise<TenderDetails> {
    const response = await axiosInstance.get<ApiResponse<TenderDetails>>(`${BASE_URL}/dashboard/tender-details`, { params: { workId } });
    return response.data.data;
  },

  async getWorkTypeWorks(workTypeId: number): Promise<{ schemeBreakdown: any[]; works: any[] }> {
    const response = await axiosInstance.get<ApiResponse<{ schemeBreakdown: any[]; works: any[] }>>(`${BASE_URL}/dashboard/work-type-works`, { params: { workTypeId } });
    return response.data.data;
  },

  async getSachivalayamBoundaries(udaId: number): Promise<any> {
    const response = await axiosInstance.get<ApiResponse<any>>(`${BASE_URL}/dashboard/sachivalayam-boundaries`, { params: { udaId } });
    return response.data.data;
  },

  async getRoadNetwork(svmId: number): Promise<any> {
    const response = await axiosInstance.get<ApiResponse<any>>(`${BASE_URL}/dashboard/road-network`, { params: { svmId } });
    return response.data.data;
  },

  async getWardInfrastructureSummary(svmId: number): Promise<any> {
    const response = await axiosInstance.get<ApiResponse<any>>(`${BASE_URL}/dashboard/ward-infrastructure-summary`, { params: { svmId } });
    return response.data.data;
  },

  async getWaterBodies(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/water-bodies`, { params: { svmId } });
    return response.data.data;
  },

  async getPublicInfrastructure(svmId: number, categoryId?: number): Promise<any[]> {
    const params: Record<string, any> = { svmId };
    if (categoryId) params.categoryId = categoryId;
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/public-infrastructure`, { params });
    return response.data.data;
  },

  async getLandParcels(svmId: number): Promise<any> {
    const response = await axiosInstance.get<ApiResponse<any>>(
      `${BASE_URL}/dashboard/land-parcels`, { params: { svmId } }
    );
    return response.data.data;
  },

  async getAllInfrastructureLayers(svmId: number): Promise<{ streetLights: any[]; manholes: any[]; drains: any[]; footpaths: any[]; medians: any[] }> {
    const response = await axiosInstance.get<ApiResponse<{ streetLights: any[]; manholes: any[]; drains: any[]; footpaths: any[]; medians: any[] }>>(`${BASE_URL}/dashboard/infrastructure-layers`, { params: { svmId } });
    return response.data.data;
  },

  async getInfrastructureGeoJSON(svmId: number, category: string): Promise<any> {
    const response = await axiosInstance.get<ApiResponse<any>>(`${BASE_URL}/dashboard/infrastructure-geojson`, { params: { svmId, category } });
    return response.data.data;
  },

  async getStreetLights(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/street-lights`, { params: { svmId } });
    return response.data.data;
  },

  async getManholes(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/manholes`, { params: { svmId } });
    return response.data.data;
  },

  async getDrains(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/drains`, { params: { svmId } });
    return response.data.data;
  },

  async getFootpaths(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/footpaths`, { params: { svmId } });
    return response.data.data;
  },

  async getMedians(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/medians`, { params: { svmId } });
    return response.data.data;
  },

  async getHandPumps(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/hand-pumps`, { params: { svmId } });
    return response.data.data;
  },

  async getOverheadTanks(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/overhead-tanks`, { params: { svmId } });
    return response.data.data;
  },

  async getTubeWells(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/tube-wells`, { params: { svmId } });
    return response.data.data;
  },

  async getPublicTaps(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/public-taps`, { params: { svmId } });
    return response.data.data;
  },

  async getWaterTanks(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/water-tanks`, { params: { svmId } });
    return response.data.data;
  },

  async getVentShafts(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/vent-shafts`, { params: { svmId } });
    return response.data.data;
  },

  async getGarbageCollectionPoints(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/garbage-collection-points`, { params: { svmId } });
    return response.data.data;
  },

  async getWaterTreatmentPlants(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/water-treatment-plants`, { params: { svmId } });
    return response.data.data;
  },

  async getParksAndGardens(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/parks-and-gardens`, { params: { svmId } });
    return response.data.data;
  },

  async getBridgesAndFlyovers(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/bridges-and-flyovers`, { params: { svmId } });
    return response.data.data;
  },

  async getSewerageNetwork(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/sewerage-network`, { params: { svmId } });
    return response.data.data;
  },

  async getTrafficSquares(svmId: number): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/dashboard/traffic-squares`, { params: { svmId } });
    return response.data.data;
  },

  async getGroundedSummary(filters?: WMSFilters): Promise<any> {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get<ApiResponse<any>>(
      `${BASE_URL}/dashboard/grounded-summary`, { params }
    );
    return response.data.data;
  }
};

/**
 * Works API
 */
export type LifecycleCellType = 'date' | 'amount' | 'pct' | 'bool' | 'status' | 'contractor';
export interface LifecycleFact { label: string; value: string | number | boolean | null; type?: LifecycleCellType; id?: number | null }
export interface LifecycleTable {
  title?: string;
  columns: Array<{ key: string; label: string; type?: LifecycleCellType; idKey?: string }>;
  rows: Array<Record<string, any>>;
}
export interface LifecycleStage {
  key: string;
  title: string;
  state: 'done' | 'active' | 'pending';
  date: string | null;
  ref: string | null;
  amount: number | null;
  facts: LifecycleFact[];
  table: LifecycleTable | null;
  emptyText: string;
}
export interface WorkLifecycle {
  work: { workId: number; workCode: string; workName: string; status: string | null; physicalPct: number | null };
  totals: { sanctioned: number | null; contract: number | null; measured: number; billed: number; paid: number; due: number };
  stages: LifecycleStage[];
}

export const worksApi = {
  async getAll(filters?: WMSFilters, page = 1, limit = 50, orderBy = 'i_ts', orderDirection = 'DESC') {
    const params = {
      ...buildFilterParams(filters),
      page,
      limit,
      orderBy,
      orderDirection
    };
    const response = await axiosInstance.get<ApiResponse<WorkSummary[]>>(`${BASE_URL}/works`, { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  },

  async getById(workId: string | number): Promise<WorkSummary> {
    const response = await axiosInstance.get<ApiResponse<WorkSummary>>(`${BASE_URL}/works/${workId}`);
    return response.data.data;
  },

  // Schedule-Targets abstract drill-down: BOQ descriptions → Types of Work →
  // components (sub-activities) with planned/actual dates, amounts and progress.
  // Powers the Work Profile "Component Wise Physical Progress" section.
  async getBoqBreakdown(workId: string | number): Promise<any> {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/boq/breakdown`);
    return response.data.data;
  },

  // Raw BOQ line items for a work (dscn_tx, amount, sub-sector, etc.). Used as a
  // fallback for the Description-wise view when the schedule-targets breakdown
  // returns nothing (e.g. components/templates not yet configured).
  /**
   * Ground a work — record that it has physically started on site.
   *
   * POST /works/:id/ground existed on the server with no client method and no button,
   * so the only way to ground a work was the bulk status dialog on Work Schedule. The
   * server validates AWARDED -> GROUNDED and, in the same transaction, grounds the
   * work's APPROVED designs (designs still under review are left alone).
   */
  async ground(workId: string | number): Promise<any> {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/ground`, {});
    return response.data;
  },

  async getBoqList(workId: string | number): Promise<any[]> {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/boq`);
    return response.data.data || [];
  },

  // AE/EE/SE + contractors for a work, derived from its package's bifurcation groups.
  async getOfficers(workId: string | number): Promise<{
    package: { code: string; name: string } | null;
    ee: Array<{ name: string; office?: string; cfms?: string }>;
    se: Array<{ name: string; office?: string; cfms?: string }>;
    contractors: Array<{ name: string }>;
  }> {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/officers`);
    return response.data.data;
  },

  async create(workData: Partial<WorkSummary>): Promise<WorkSummary> {
    const response = await axiosInstance.post<ApiResponse<WorkSummary>>(`${BASE_URL}/works`, workData);
    return response.data.data;
  },

  async update(workId: string | number, workData: Partial<WorkSummary>): Promise<WorkSummary> {
    const response = await axiosInstance.put<ApiResponse<WorkSummary>>(`${BASE_URL}/works/${workId}`, workData);
    return response.data.data;
  },

  async bulkUpdateStatus(workIds: Array<string | number>, wrk_sts_id: number): Promise<{ updated: number; workIds: number[] }> {
    const response = await axiosInstance.put<ApiResponse<{ updated: number; workIds: number[] }>>(`${BASE_URL}/works/bulk-status`, { workIds, wrk_sts_id });
    return response.data.data;
  },

  async bulkUpdateAgreementDate(workIds: Array<string | number>, agrmnt_dt: string | null): Promise<{ updated: number; workIds: number[] }> {
    const response = await axiosInstance.put<ApiResponse<{ updated: number; workIds: number[] }>>(`${BASE_URL}/works/bulk-agreement-date`, { workIds, agrmnt_dt });
    return response.data.data;
  },

  async delete(workId: string | number, hard = false): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/works/${workId}`, { params: { hard } });
  },

  async getHistory(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/history`);
    return response.data.data;
  },

  async getDocuments(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/documents`);
    return response.data.data;
  },

  async getPayments(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/payments`);
    return response.data.data;
  },

  async getProgress(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/progress`);
    return response.data.data;
  },

  /** Work Profile — Lifecycle tab: every stage from proposal to final payment. */
  async getLifecycle(workId: string | number): Promise<WorkLifecycle> {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/lifecycle`);
    return response.data.data;
  },

  async getProposals(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/proposals`);
    return response.data.data as Array<{
      prpsl_id: number;
      prpsl_cd: string;
      prpsl_nm: string;
      estmt_cst_at: number;
      prpsl_sts_cd: string;
      prpsl_dt: string | null;
      lctn_tx: string | null;
      prpsl_wrk_id: number;
      allctd_at: number;
      prcntge_nu: number;
      rel_type_cd: string;
      rmrks_tx: string | null;
    }>;
  },

  async attachProposals(workId: string | number, prpslIds: number[]) {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/proposals`, { prpslIds });
    return response.data.data;
  },

  async detachProposal(workId: string | number, prpslId: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/works/${workId}/proposals/${prpslId}`);
    return response.data.data;
  }
};

/**
 * Masters API
 */
export const mastersApi = {
  // Declared on the object literal (not the Object.assign extension block below) so
  // TypeScript can see it — callers of the extension-block methods get TS2339.
  async getBGTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/bg-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getInspectionTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/inspection-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getInspectionStatuses(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/inspection-statuses`, { params: { activeOnly } });
    return response.data.data;
  },

  async getInspectionRatings(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/inspection-ratings`, { params: { activeOnly } });
    return response.data.data;
  },

  async getWorkCategories(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-categories`, { params: { activeOnly } });
    return response.data.data;
  },

  async getWorkTypes(activeOnly = true, categoryId?: number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-types`, { params: { activeOnly, categoryId } });
    return response.data.data;
  },

  async getWorkStatuses(activeOnly = true, phase?: string) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-statuses`, { params: { activeOnly, phase } });
    return response.data.data;
  },

  async getSchemes(activeOnly = true, schemeType?: string, govtLevel?: string): Promise<SchemeMaster[]> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/schemes`, { params: { activeOnly, schemeType, govtLevel } });
    return response.data.data;
  },

  async getFinancialYears(activeOnly = true, currentOnly = false): Promise<FinancialYearMaster[]> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/financial-years`, { params: { activeOnly, currentOnly } });
    return response.data.data;
  },

  // Event Tag master — feeds the "Event Tag" dropdown on the Create Work form.
  async getEventTags(): Promise<Array<{ evnt_tag_id: number; evnt_tag_cd: string; evnt_tag_nm: string }>> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/event-tags`);
    return response.data.data || [];
  },

  // Pushkaralu Activity master — feeds the "Activity" dropdown on the Create Work form.
  async getPushkaraluActivities(): Promise<Array<{ pushkaralu_actvty_id: number; pushkaralu_actvty_cd: string; pushkaralu_actvty_nm: string }>> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/pushkaralu-activities`);
    return response.data.data || [];
  },

  async getDepartments(activeOnly = true, parentId?: number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/departments`, { params: { activeOnly, parentId } });
    return response.data.data;
  },

  async getDepartmentsList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/departments/list`);
    return response.data.data;
  },

  async getFinancialYearsList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/financial-years/list`);
    return response.data.data;
  },

  async getSchemesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/schemes/list`);
    return response.data.data;
  },

  // Distinct PIA (Project Implementing Agency) values pulled from work attrs.
  // Returns [{ pia: '<name>' }, ...].
  async getPiaList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/pia-list`);
    return response.data.data;
  },

  // Distinct UDAs that actually have works (from ds_wms.adm_ulb_lst_t).
  // Returns [{ ulb_id, ulb_nm }, ...] — used by the work-schedule UDA filter
  // so it lists only UDAs present in the table, not the full UDA master.
  async getUlbList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/ulb-list`);
    return response.data.data;
  },

  // Pushkaralu host UDAs (adm_ulb_lst_t.is_pushkaralu_in) for the Create Work
  // dropdown. Returns [{ admn_unt_id, admn_unt_nm }] — DB-driven, no hardcoding.
  async getPushkaraluUlbs() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/pushkaralu-ulbs`);
    return response.data.data;
  },

  // Distinct circles that actually have works (from ds_wms.adm_crcl_lst_t).
  // Returns [{ crcl_id, crcl_nm }, ...] — used by the work-schedule Circle filter
  // so it lists only circles present in the table, not the full circle master.
  async getCirclesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/circle-list`);
    return response.data.data;
  },

  async getWorkTypesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-types/list`);
    return response.data.data;
  },

  async getWorkCategoriesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-categories/list`);
    return response.data.data;
  },

  async getWorkStatusesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-statuses/list`);
    return response.data.data;
  },

  async getWorkNaturesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-natures/list`);
    return response.data.data;
  },

  async getDesignations(activeOnly = true, departmentId?: number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/designations`, { params: { activeOnly, departmentId } });
    return response.data.data;
  },

  /* ---- Project Configuration pick lists (ported from APUFIDC) ---- */

  /**
   * Roles selectable as a workflow desk owner or initiator. DTCP roles are per UDA,
   * so each carries its UDA in `projects` and the designer shows
   * "Executive Engineer (VMRDA)". user_count counts direct AND position holders.
   */
  async getWorkflowRoles(): Promise<Array<{
    rle_id: number;
    rle_nm: string;
    user_count?: number;
    in_my_project?: boolean;
    projects: Array<{ prjct_id: number; prjct_cd: string; prjct_nm: string }>;
    dprt_ids?: number[];
  }>> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/workflow-roles`);
    // A BIGINT arriving as a string breaks every `rle_id === role_id` comparison.
    return (response.data.data || []).map((r: any) => ({
      ...r,
      rle_id: Number(r.rle_id),
      dprt_ids: (r.dprt_ids || []).map(Number),
    }));
  },

  /** Departments for the Workflow Designer — the WMS department master (dswms_eg_department). */
  async getWorkflowDepartments(): Promise<Array<{ dprt_id: number; dprt_cd: string; dprt_nm: string }>> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/workflow-departments`);
    return response.data.data || [];
  },

  /** Project stages for the Project Configuration stage cards. */
  async getWorkStages(): Promise<Array<{
    wrk_stge_id: number; wrk_stge_cd: string; wrk_stge_nm: string;
    dscn_tx?: string; icon_cd?: string; clr_cd?: string; sqnce_nu?: number;
    is_start_in?: boolean; is_end_in?: boolean; allow_skip_in?: boolean;
  }>> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/work-stages`);
    return response.data.data || [];
  },

  async searchEmployees(params: { search?: string; tenantid?: string; designation_id?: number; page?: number; limit?: number }) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/employees/search`, { params });
    return response.data;
  },

  async getEmployeeTenants() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/employees/tenants`);
    return response.data.data;
  },

  async searchPositions(params: { tenantid?: string; department_id?: number; search?: string; page?: number; limit?: number }) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/positions/search`, { params });
    return response.data;
  },

  async getSachivalayamList(udaId: number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sachivalayam/list`, { params: { udaId } });
    return response.data.data;
  },

  async getAdminUnits(activeOnly = true, unitType?: string, parentId?: number): Promise<ZoneMaster[] | WardMaster[]> {
    const response = await axiosInstance.get(`${BASE_URL}/masters/admin-units`, { params: { activeOnly, unitType, parentId } });
    return response.data.data;
  },

  // Zones and wards, remapped to the Zone / Ward shape the forms consume.
  //
  // getAdminUnits returns dswms_admn_unt_lst_t rows verbatim (admn_unt_id /
  // admn_unt_nm / prnt_unt_id), but ProposalForm renders zone.zone_id / zone.zone_nm
  // and filters wards on ward.zone_id (masterTypes.ts:54-65, ProposalForm.tsx:722-745).
  // Without this mapping every option rendered with an undefined value and empty label,
  // so the dropdown looked empty even with 63 zones present.
  //
  // getAdminUnits is deliberately left untouched: BulkUploadWorksForm, CreateWorkForm,
  // EstimateForm and old-data-entry call it with 'UDA'/'DISTRICT' and rely on the raw
  // admn_unt_* shape.
  async getZones() {
    const rows = (await mastersApi.getAdminUnits(true, 'zone')) as any[];
    return (rows ?? []).map((r: any) => ({
      zone_id: r.admn_unt_id, zone_cd: r.admn_unt_cd, zone_nm: r.admn_unt_nm,
    }));
  },

  async getWards() {
    const rows = (await mastersApi.getAdminUnits(true, 'ward')) as any[];
    return (rows ?? []).map((r: any) => ({
      ward_id: r.admn_unt_id, ward_cd: r.admn_unt_cd, ward_nm: r.admn_unt_nm,
      zone_id: r.prnt_unt_id,
    }));
  },

  // ── Finance masters (legacy ERP finance module, NOT ds-wms) ───────────────
  // These four back the Budgetary Details section of the Proposal form. They live
  // under /apiv1/finance/* (apiRoutes.ts:85), a sibling of ds-wms, so they do not
  // use BASE_URL. Each returns the legacy eGov shape { id, name, code }; the forms
  // expect DTCP-convention keys, so each response is mapped here.
  //
  // The routes are permission-guarded (authService.authorize('fund','read') etc.),
  // so a profile without finance read access gets a 403 — masterListStore uses
  // Promise.allSettled, which turns that into an empty dropdown rather than a crash.

  async getFundingAgencies() {
    const r = await axiosInstance.get('finance/fund');
    return (r.data?.data ?? []).map((x: any) => ({
      fndg_agncy_id: x.id, fndg_agncy_cd: x.code, fndg_agncy_nm: x.name,
    }));
  },

  async getFunctionCenters() {
    const r = await axiosInstance.get('finance/function');
    return (r.data?.data ?? []).map((x: any) => ({
      fnctn_id: x.id, fnctn_cd: x.code, fnctn_nm: x.name,
    }));
  },

  // ds_budgetgroup has no `code` column — it carries majorcode/maxcode/mincode GL
  // references instead, which the controller exposes as *_glcode. Its `name` already
  // embeds the GL code (e.g. "1503002-Sale of Old news papers").
  async getBudgetHeads() {
    const r = await axiosInstance.get('finance/budget-group');
    return (r.data?.data ?? []).map((x: any) => ({
      bdgt_hd_id: x.id,
      bdgt_hd_cd: x.majorcode_glcode ?? x.maxcode ?? null,
      bdgt_hd_nm: x.name,
    }));
  },

  async getSubSchemes() {
    const r = await axiosInstance.get('finance/subscheme');
    return (r.data?.data ?? []).map((x: any) => ({
      sub_schme_id: x.id, sub_schme_cd: x.code, sub_schme_nm: x.name,
      schme_nm: x.schemename, schme_cd: x.schemecode,
    }));
  },

  // Migration 158 — these three replaced hardcoded <option> tags on the Technical
  // Sanction form. The SOR type list previously had no Urban Greenery entry.
  async getSorTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sor-types`, { params: { activeOnly } });
    return response.data.data;
  },

  // axis: 'NATURE' (Original/Revised/Supplementary) or 'DETAIL' (Preliminary/Detailed).
  // Omit for both.
  async getEstimateTypes(activeOnly = true, axis?: string) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/estimate-types`, { params: { activeOnly, axis } });
    return response.data.data;
  },

  // Migration 159 — replaced the hardcoded PENALTY_TYPES array in PenaltyEntry.tsx.
  // The four finance masters, read through ds-wms so the Setup screens get the raw
  // rows (id / code / name / isactive). Distinct from getFundingAgencies() and friends
  // above, which call /apiv1/finance/* and remap the fields for the proposal form.
  async getFundingAgenciesMaster(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/funding-agencies`, { params: { activeOnly } });
    return response.data.data;
  },

  async getFunctionCentersMaster(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/function-centers`, { params: { activeOnly } });
    return response.data.data;
  },

  async getBudgetHeadsMaster(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/budget-heads`, { params: { activeOnly } });
    return response.data.data;
  },

  async getSubSchemesMaster(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sub-schemes`, { params: { activeOnly } });
    return response.data.data;
  },

  // Sub-sectors for the Setup master screen. The dropdown elsewhere in the module uses
  // /cnstrn-config/sub-sectors, which is active-only by design; this one passes
  // activeOnly through so Setup can show and reactivate a retired sub-sector.
  async getSubSectorsMaster(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sub-sectors`, { params: { activeOnly } });
    return response.data.data;
  },

  async getPwdClasses(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/pwd-classes`, { params: { activeOnly } });
    return response.data.data;
  },

  // Field designations for measurement recording. Deliberately NOT getDesignations(),
  // which serves the ERP-wide ds_dsgn_lst_t (49,112 rows).
  async getWmsDesignations(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/wms-designations`, { params: { activeOnly } });
    return response.data.data;
  },

  async getAdvanceTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/advance-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getPenaltyTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/penalty-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getSorYears() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sor-years`);
    return response.data.data;
  },

  async getSanctionAuthorities(activeOnly = true, sanctionType?: string) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sanction-authorities`, { params: { activeOnly, sanctionType } });
    return response.data.data;
  },

  async getUOM(activeOnly = true, uomType?: string) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/uom`, { params: { activeOnly, uomType } });
    return response.data.data;
  },

  // Generic master CRUD
  async createMaster(masterType: string, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/masters/${masterType}`, data);
    return response.data.data;
  },

  async updateMaster(masterType: string, id: number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/masters/${masterType}/${id}`, data);
    return response.data.data;
  },

  async deleteMaster(masterType: string, id: number, hard = false) {
    await axiosInstance.delete(`${BASE_URL}/masters/${masterType}/${id}`, { params: { hard } });
  }
};

/**
 * Helper function to build filter params
 */
function buildFilterParams(filters?: WMSFilters): Record<string, any> {
  if (!filters) return {};

  const params: Record<string, any> = {};

  if (filters.financialYear) params.financialYear = filters.financialYear;
  if (filters.circleId) params.circleId = filters.circleId;
  if (filters.district) params.district = filters.district;
  if (filters.udaId) params.udaId = Array.isArray(filters.udaId) ? filters.udaId.join(',') : filters.udaId;
  if (filters.zoneId) params.zoneId = Array.isArray(filters.zoneId) ? filters.zoneId.join(',') : filters.zoneId;
  if (filters.wardId) params.wardId = Array.isArray(filters.wardId) ? filters.wardId.join(',') : filters.wardId;
  if (filters.sachivalayamId) params.sachivalayamId = filters.sachivalayamId;
  if (filters.schemeId) params.schemeId = Array.isArray(filters.schemeId) ? filters.schemeId.join(',') : filters.schemeId;
  if (filters.workType) params.workType = Array.isArray(filters.workType) ? filters.workType.join(',') : filters.workType;
  if (filters.workCategory) params.workCategory = Array.isArray(filters.workCategory) ? filters.workCategory.join(',') : filters.workCategory;
  if (filters.workNature) params.workNature = Array.isArray(filters.workNature) ? filters.workNature.join(',') : filters.workNature;
  if (filters.workStatus) params.workStatus = Array.isArray(filters.workStatus) ? filters.workStatus.join(',') : filters.workStatus;
  if (filters.contractorId) params.contractorId = filters.contractorId;
  if (filters.dateRange) {
    params.fromDate = filters.dateRange.from;
    params.toDate = filters.dateRange.to;
  }
  if (filters.valueRange) {
    params.valueMin = filters.valueRange.min;
    params.valueMax = filters.valueRange.max;
  }
  if (filters.progressRange) {
    params.progressMin = filters.progressRange.min;
    params.progressMax = filters.progressRange.max;
  }
  if (filters.excludeNonPackaged) params.excludeNonPackaged = 'true';

  return params;
}

/**
 * Estimates API
 */
export const estimatesApi = {
  async getAll(
    filters?: WMSFilters & {
      search?: string;
      status?: string;
      estimateType?: string;
      workId?: number | string;
      // true → only estimates with wrk_id IS NULL (used by Create Work form)
      noWork?: boolean;
      // Design state of the estimate's work: none | uploaded | approved | any
      designState?: 'none' | 'uploaded' | 'approved' | 'any';
      // true → only estimates attached to a work
      hasWork?: boolean;
    },
    page = 1,
    limit = 50
  ) {
    const extra: Record<string, any> = {};
    if (filters) {
      if (filters.search) extra.search = filters.search;
      if (filters.status) extra.status = filters.status;
      if (filters.estimateType) extra.estimateType = filters.estimateType;
      if (filters.workId !== undefined && filters.workId !== null && filters.workId !== '') extra.workId = filters.workId;
      if (filters.noWork) extra.noWork = true;
      if (filters.designState) extra.designState = filters.designState;
      if (filters.hasWork) extra.hasWork = true;
    }
    const params = { ...buildFilterParams(filters), ...extra, page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/estimates`, { params });
    return { data: response.data.data, pagination: response.data.pagination, summary: response.data.summary };
  },

  async getById(estimateId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/estimates/${estimateId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/estimates`, data);
    return response.data.data;
  },

  async update(estimateId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/estimates/${estimateId}`, data);
    return response.data.data;
  },

  async delete(estimateId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/estimates/${estimateId}`);
  },

  async getItems(estimateId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/estimates/${estimateId}/items`);
    return response.data.data;
  },

  async addItem(estimateId: string | number, itemData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/estimates/${estimateId}/items`, itemData);
    return response.data.data;
  },

  // Workflow transitions. update() no longer accepts sts_cd — the server refuses it
  // with 409 so a status cannot be set without validating the transition. Approval
  // also advances the work to EST_PREP.
  // Stage trail for an estimate. Same shape as the proposal version so one dialog
  // renders either without branching.
  async getWorkflowHistory(estimateId: string | number) {
    const response = await axiosInstance.get(
      `${BASE_URL}/estimates/${estimateId}/workflow-history`);
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; prpsl_sts_cd: string;
      created: { at: string; by: string | null };
      entries: WorkflowHistoryEntry[];
    };
  },

  // Officers this estimate can be sent to at a stage. Same shape as the proposal
  // version, so SubmitToOfficerDialog renders either without branching.
  async getWorkflowAssignees(estimateId: string | number, stage = 'SUBMITTED') {
    const response = await axiosInstance.get(
      `${BASE_URL}/estimates/${estimateId}/workflow-assignees`, { params: { stage } });
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; stage: string; uda_id: number | null;
      configured: boolean; nxt_actn_tx: string | null;
      designation: { id: number; name: string; rank: number } | null;
      default: WorkflowAssignee | null;
      candidates: WorkflowAssignee[];
      /* The whole assignable directory for this record's authority, as
       * Department -> Designation -> Employee. `candidates` above is the routing
       * matrix's answer and stays the default; this is the browse path, and it
       * carries the posts the rank ladder cannot score. */
      tree: WorkflowAssigneeDept[];
    };
  },

  async submit(estimateId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/estimates/${estimateId}/submit`, data);
    return response.data.data;
  },

  // The DEE's forward move: checked, now with the EE for sanction.
  async review(estimateId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/estimates/${estimateId}/review`, data);
    return response.data.data;
  },

  async approve(estimateId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/estimates/${estimateId}/approve`, data);
    return response.data.data;
  },

  async reject(estimateId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/estimates/${estimateId}/reject`, data);
    return response.data.data;
  },

  async reopen(estimateId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/estimates/${estimateId}/reopen`, data);
    return response.data.data;
  }
};

/**
 * Tenders API - Works in tender pipeline stages
 */
export const tendersApi = {
  async getAll(filters?: WMSFilters & { stage?: string; search?: string; goNumber?: string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit, stage: filters?.stage, search: filters?.search, goNumber: filters?.goNumber };
    const response = await axiosInstance.get(`${BASE_URL}/tenders`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getAllSummary() {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/tenders/summary`);
    return { data: response.data.data, total: response.data.total };
  },

  async getById(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${workId}`);
    return response.data.data;
  },

  async getStageCounts() {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/stage-counts`);
    return response.data;
  },

  async getByStage(stage: string, filters?: WMSFilters & { search?: string; goNumber?: string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit, search: filters?.search, goNumber: filters?.goNumber };
    const response = await axiosInstance.get(`${BASE_URL}/tenders/stage/${stage}`, { params });
    return { data: response.data.data, pagination: response.data.pagination, stage: response.data.stage };
  },

  async getExecutionSubStages() {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/execution-substages`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders`, data);
    return response.data.data;
  },

  async update(tenderId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/${tenderId}`, data);
    return response.data.data;
  },

  async delete(tenderId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/tenders/${tenderId}`);
  },

  async getBidders(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/bidders`);
    return response.data.data;
  },

  async addBidder(tenderId: string | number, bidderData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/${tenderId}/bidders`, bidderData);
    return response.data.data;
  },

  async updateStatus(tenderId: string | number, status: string) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/${tenderId}/status`, { status });
    return response.data.data;
  },

  async directAward(tenderId: string | number, data: { cntrctr_id: number; call_id?: number }) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/${tenderId}/direct-award`, data);
    return response.data.data;
  },

  async getTenderDetails(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/details`);
    return response.data.data;
  },

  async updateBidder(bidderId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/bidders/${bidderId}`, data);
    return response.data.data;
  },

  async deleteBidder(bidderId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/tenders/bidders/${bidderId}`);
  },

  async getCommittee(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/committee`);
    return response.data.data;
  },

  async saveCommittee(tenderId: string | number, members: Record<string, any>[]) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/${tenderId}/committee`, { members });
    return response.data.data;
  },

  async getEvaluations(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/evaluations`);
    return response.data.data;
  },

  async createEvaluation(tenderId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/${tenderId}/evaluations`, data);
    return response.data.data;
  },

  async updateEvaluation(evalId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/evaluations/${evalId}`, data);
    return response.data.data;
  },

  async createLOA(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/loa`, data);
    return response.data.data;
  },

  async updateLOA(loaId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/loa/${loaId}`, data);
    return response.data.data;
  },

  // Tender Calls (1st, 2nd, 3rd call support)
  async getCalls(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/calls`);
    return response.data.data;
  },

  async createCall(tenderId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/${tenderId}/calls`, data);
    return response.data.data;
  },

  async updateCall(callId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/calls/${callId}`, data);
    return response.data.data;
  },

  async updateCallStatus(callId: string | number, status: string) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/calls/${callId}/status`, { status });
    return response.data.data;
  },

  // Bank Guarantees (tender-level BG CRUD)
  // dswms_bg_lst_t has no tndr_id — guarantees hang off work / contract / contractor,
  // so they are read back by workId rather than by tender.
  async getBGs(params: Record<string, any>) {
    const response = await axiosInstance.get(`${BASE_URL}/bank-guarantees`, { params });
    return response.data.data || [];
  },

  async createBG(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/bank-guarantees`, data);
    return response.data.data;
  },

  async updateBG(bgId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/tenders/bank-guarantees/${bgId}`, data);
    return response.data.data;
  },

  // Contracts / Agreement CRUD
  async createContract(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/contracts`, data);
    return response.data.data;
  },

  async updateContract(contractId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/contracts/${contractId}`, data);
    return response.data.data;
  },

  // Tender Documents
  async getDocuments(tenderId: number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/documents`);
    return response.data.data;
  },

  async uploadDocument(tenderId: number, formData: FormData) {
    const response = await axiosInstance.post(
      `${BASE_URL}/tenders/${tenderId}/documents`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  async deleteDocument(docId: number) {
    const response = await axiosInstance.delete(`${BASE_URL}/documents/${docId}`);
    return response.data;
  }
};

/**
 * Funding Pattern API - CAPEX/OPEX split with funding sources per work
 */
export interface FundingPattern {
  fndng_id?: number;
  wrk_id: number;
  capex_goi_at: number;
  capex_goap_at: number;
  capex_ulb_at: number;
  capex_15fc_at: number;
  capex_ppp_at: number;
  capex_totl_at: number;
  opex_goi_at: number;
  opex_goap_at: number;
  opex_ulb_at: number;
  opex_15fc_at: number;
  opex_ppp_at: number;
  opex_totl_at: number;
  totl_prjct_cst_at: number;
  src_cd?: string;
}

export const fundingPatternApi = {
  async getByWorkId(workId: number): Promise<FundingPattern | null> {
    const response = await axiosInstance.get(`${BASE_URL}/funding-pattern/${workId}`);
    return response.data.data;
  },

  async getByWorkIds(workIds: number[]): Promise<FundingPattern[]> {
    const response = await axiosInstance.post(`${BASE_URL}/funding-pattern/bulk`, { workIds });
    return response.data.data || [];
  },

  async upsert(workId: number, data: Partial<FundingPattern>): Promise<FundingPattern> {
    const response = await axiosInstance.put(`${BASE_URL}/funding-pattern/${workId}`, data);
    return response.data.data;
  },

  async bulkUpsert(patterns: Partial<FundingPattern>[]): Promise<FundingPattern[]> {
    const response = await axiosInstance.post(`${BASE_URL}/funding-pattern/bulk-upsert`, { patterns });
    return response.data.data || [];
  }
};

/**
 * Budget Entry API — AMRUT 2.0 Budget-Details funding pattern per SLS component.
 * Amounts are ₹ in RUPEES. The two totals are recomputed server-side.
 */
export interface SlsComponent {
  sls_cmpnt_id: number;
  sls_cd: string;
  cmpnt_nm: string;
  fndng_ptrn_tx: string | null;
  ctgry_tx?: string | null;
  sqnce_nu: number;
}

export interface BudgetEntry {
  bdgt_entry_id: number;
  /** NULL for schemes without SLS codes (AIIB, UIDF, …) — keyed by scheme alone. */
  sls_cmpnt_id: number | null;
  sls_cd: string | null;
  cmpnt_nm: string | null;
  fndng_ptrn_tx: string | null;
  schme_id: number | null;
  schme_cd: string | null;
  schme_nm: string | null;
  capex_goi_at: number;
  capex_goap_at: number;
  capex_ulb_at: number;
  capex_15fc_at: number;
  capex_ppp_at: number;
  /** AIIB loan share (₹ rupees) — non-AMRUT schemes only. */
  capex_aiib_at?: number;
  /** Others share (₹ rupees) — e.g. Soft Loan. */
  capex_othr_at?: number;
  capex_totl_at: number;
  opex_ulb_at: number;
  totl_prjct_cst_at: number;
  // Releases & Balance (₹ in Rupees). *_totl_at and bal_* are auto-computed server-side.
  ms_goi_at: number;
  ms_aiib_at?: number;
  state_rls_goi_at: number;
  state_rls_aiib_at?: number;
  state_rls_goap_at: number;
  state_rls_totl_at: number;
  ia_rls_goi_at: number;
  ia_rls_aiib_at?: number;
  ia_rls_goap_at: number;
  ia_rls_totl_at: number;
  bal_goi_at: number;
  bal_aiib_at?: number;
  bal_goap_at: number;
  bal_totl_at: number;
  // Government Order / date details (Sanctions & Releases form)
  go_no_tx: string | null;
  go_dt: string | null;
  snctn_dt: string | null;
  rls_dt: string | null;
  rmrks_tx: string | null;
  /** Documents filed against this entry's dated history rows. */
  txn_attch_n?: number;
  /** History rows created with the entry (returned by create only). */
  txns?: BudgetTxn[];
}

/**
 * One dated Mother Sanction ('MS', GoI / AIIB) or release by the State Govt.
 * ('STATE', GoI + AIIB + GoAP/Others) under a Budget Allocation entry. ₹ RUPEES.
 * The entry's ms_* / state_rls_* are the sum of these rows.
 */
export interface BudgetTxn {
  bdgt_txn_id: number;
  bdgt_entry_id: number;
  txn_type_cd: 'MS' | 'STATE';
  goi_at: number;
  aiib_at?: number;
  goap_at: number;
  totl_at: number;
  /** yyyy-mm-dd */
  txn_dt: string | null;
  go_no_tx: string | null;
  rmrks_tx?: string | null;
  attch_n?: number;
}

export type BudgetTxnInput = Pick<BudgetTxn, 'txn_type_cd' | 'goi_at' | 'aiib_at' | 'goap_at' | 'txn_dt' | 'go_no_tx'> & { rmrks_tx?: string | null };

export type BudgetEntryInput = Omit<
  BudgetEntry,
  'bdgt_entry_id' | 'sls_cd' | 'cmpnt_nm' | 'fndng_ptrn_tx' | 'capex_totl_at' | 'totl_prjct_cst_at'
  | 'state_rls_totl_at' | 'ia_rls_totl_at' | 'bal_goi_at' | 'bal_aiib_at' | 'bal_goap_at' | 'bal_totl_at'
>;

/**
 * Project Cost API — ULB-wise project details (AMRUT 2.0 sheet).
 * Amounts are ₹ in RUPEES; *_totl_at columns are computed server-side.
 */
export interface ProjectCost {
  /** Alias of wrk_id — entries are rows of ds_wms.wms_wrk_lst_t. */
  prjct_cost_id: number;
  wrk_id?: number;
  /** Scheme of the work — decides which budget its releases are checked against. */
  schme_id?: number | null;
  schme_cd?: string | null;
  schme_nm?: string | null;
  /** How many active release tranches recorded against this project. */
  rls_n?: number;
  /** Count of PROJECT_RELEASE attachments across this project's tranches. */
  rls_attch_n?: number;
  /** Latest release date across this project's tranches (source-of-truth). */
  last_rls_dt?: string | null;
  // ULB information
  entry_dt: string | null;
  dstrt_id: number | null;
  dstrct_nm: string | null;
  /** The UDA the project belongs to. ulb_id / ulb_nm carry the same UDA (older field names). */
  uda_id?: number | null;
  uda_nm?: string | null;
  ulb_id: number | null;
  ulb_nm: string;
  ia_nm: string | null;
  mla_con_tx: string | null;
  mp_con_tx: string | null;
  // Project information
  sls_cmpnt_id: number | null;
  sls_cd: string | null;
  prjct_id_tx: string;
  prjct_ttl_tx: string | null;
  sectr_cd: string | null;
  swap_tx: string | null;
  ctgry_tx: string | null;
  tier_tx: string | null;
  pc_goi_at: number; pc_goap_at: number; pc_ulb_at: number; pc_15fc_at: number; pc_ppp_at: number;
  pc_othr_at?: number;
  /** AIIB loan share (AIIB-assisted schemes). */
  pc_aiib_at?: number;
  pc_capex_totl_at: number; pc_opex_ulb_at: number; pc_totl_at: number;
  fr_goi_at: number; fr_goap_at: number; fr_ulb_at: number; fr_15fc_at: number; fr_ppp_at: number;
  fr_othr_at?: number;
  fr_capex_totl_at: number;
  // Government order / milestone dates (Sanctions & Releases)
  go_no_tx?: string | null;
  go_dt?: string | null;
  snctn_dt?: string | null;
  rls_dt?: string | null;
  rmrks_tx: string | null;
}

export type ProjectCostInput = Omit<
  ProjectCost,
  'prjct_cost_id' | 'wrk_id' | 'pc_capex_totl_at' | 'pc_totl_at' | 'fr_capex_totl_at'
>;

export interface DistrictOption { dstrt_id: number; dstrt_nm: string }
export interface UlbOption { ulb_id: number; ulb_nm: string; dstrt_id?: number | null }
export interface UdaOption { uda_id: number; uda_nm: string; uda_cd?: string | null }
/** udas is the UDA master; ulbs carries the same rows under the older field names. */
export interface ProjectCostLookups { districts: DistrictOption[]; udas?: UdaOption[]; ulbs: UlbOption[]; sectors: string[]; categories?: string[]; ias: string[] }

/**
 * A project reflected from the standing work list (read-only). snctn_cr is the
 * sanction amount in Crores (snctn_at rupees / 1e7) — the figure the dashboard's
 * "Released" total is built from. Funding-pattern amounts are supplementary.
 */
export interface WorkReflect {
  wrk_id: number;
  wrk_cd: string;
  wrk_nm: string;
  schme_id: number | null;
  schme_cd: string | null;
  schme_nm: string | null;
  ulb_id: number | null;
  ulb_nm: string | null;
  dstrt_id: number | null;
  dstrt_nm: string | null;
  pia_id: number | null;
  pia_cd: string | null;
  pia_nm: string | null;
  sectr_cd: string | null;
  sls_cd: string | null;
  sls_cmpnt_id: number | null;
  prjct_id: string | null;
  // Project-cost fields the /works query also returns (see project-cost.controller).
  prjct_id_tx: string | null;
  prjct_ttl_tx: string | null;
  ia_nm: string | null;
  mla_con_tx: string | null;
  mp_con_tx: string | null;
  ctgry_tx: string | null;
  tier_tx: string | null;
  swap_tx: string | null;
  entry_dt: string | null;
  go_no_tx: string | null;
  go_dt: string | null;
  snctn_dt: string | null;
  rls_dt: string | null;
  rmrks_tx: string | null;
  /** Number of release tranches filed against this project. */
  rls_n: number;
  last_rls_dt: string | null;
  attch_n: number;
  rls_attch_n: number;
  snctn_at: number;
  snctn_cr: number;
  orgnl_estmt_at: number;
  rvsd_estmt_at: number;
  // entry-form funding breakdown (₹ in Cr as stored on the works)
  capex_goi_at: number;
  capex_goap_at: number;
  capex_ulb_at: number;
  capex_15fc_at: number;
  capex_ppp_at: number;
  capex_totl_at: number;
  opex_ulb_at: number;
  opex_totl_at: number;
  totl_prjct_cst_at: number;
  fr_capex_totl_at: number;
  /** AIIB loan share (₹ Cr) from cstm_flds_jn — null outside AIIB works. */
  aiib_asst_at?: number | null;
}

export const projectCostApi = {
  async list(): Promise<ProjectCost[]> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost`);
    return response.data.data || [];
  },
  /** Read-only reflection of every scheme work (project details already in WMS). */
  async works(): Promise<WorkReflect[]> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost/works`);
    return response.data.data || [];
  },
  /** Fetch a single project-cost entry by its id (= wrk_id). Returns null if absent. */
  async get(id: number): Promise<ProjectCost | null> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost/${id}`);
    return response.data.data || null;
  },
  async getLookups(): Promise<ProjectCostLookups> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost/lookups`);
    return response.data.data || { districts: [], ulbs: [], sectors: [], categories: [], ias: [] };
  },
  async create(data: ProjectCostInput): Promise<ProjectCost> {
    const response = await axiosInstance.post(`${BASE_URL}/project-cost`, data);
    return response.data.data;
  },
  async update(id: number, data: ProjectCostInput): Promise<ProjectCost> {
    const response = await axiosInstance.put(`${BASE_URL}/project-cost/${id}`, data);
    return response.data.data;
  },
  async remove(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/project-cost/${id}`);
  },
  /* Release history (tranches) — one row per release event against a project. */
  /**
   * Every release across all projects — the Financial dashboard's Expenditure
   * card. Amounts are ₹ RUPEES as stored; divide by 1e7 for crore.
   */
  async listAllReleases(): Promise<ProjectRelease[]> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost/releases`);
    return response.data.data || [];
  },

  async listReleases(prjctCostId: number): Promise<ProjectRelease[]> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost/${prjctCostId}/releases`);
    return response.data.data || [];
  },
  async createRelease(prjctCostId: number, data: Partial<ProjectRelease>): Promise<{ rls_id: number }> {
    const response = await axiosInstance.post(`${BASE_URL}/project-cost/${prjctCostId}/releases`, data);
    return response.data.data;
  },
  async updateRelease(rlsId: number, data: Partial<ProjectRelease>): Promise<void> {
    await axiosInstance.put(`${BASE_URL}/project-cost/releases/${rlsId}`, data);
  },
  async removeRelease(rlsId: number): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/project-cost/releases/${rlsId}`);
  },

  /* Utilization history — contractor recording utilization against projects having releases */
  async listAllUtilizations(): Promise<ProjectUtilization[]> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost/utilizations`);
    return response.data.data || [];
  },

  async listUtilizations(prjctCostId: number): Promise<ProjectUtilization[]> {
    const response = await axiosInstance.get(`${BASE_URL}/project-cost/${prjctCostId}/utilizations`);
    return response.data.data || [];
  },

  async createUtilization(prjctCostId: number, data: Partial<ProjectUtilization>): Promise<{ utlztn_id: number }> {
    const response = await axiosInstance.post(`${BASE_URL}/project-cost/${prjctCostId}/utilizations`, data);
    return response.data.data;
  },

  async updateUtilization(utlztnId: number, data: Partial<ProjectUtilization>): Promise<void> {
    await axiosInstance.put(`${BASE_URL}/project-cost/utilizations/${utlztnId}`, data);
  },

  async removeUtilization(utlztnId: number): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/project-cost/utilizations/${utlztnId}`);
  },
};

/** One fund-release event (tranche) against a project. */
export interface ProjectRelease {
  rls_id: number;
  prjct_cost_id: number;
  fr_goi_at: number; fr_goap_at: number; fr_ulb_at: number; fr_15fc_at: number; fr_ppp_at: number;
  fr_othr_at?: number;
  fr_capex_totl_at: number;
  rls_dt: string | null;
  go_no_tx: string | null;
  rmrks_tx: string | null;
  i_ts?: string;
}

/** One fund-utilization record against a project entered by contractor. */
export interface ProjectUtilization {
  utlztn_id: number;
  prjct_cost_id: number;
  cfms_id: string | null;
  is_utlzd_in: boolean;
  utlzd_sts_cd: string;
  utlzd_at: number;
  utlztn_dt: string | null;
  /** Payment date — empty while the bill is only raised (Pending), set once paid (Utilized). */
  pymnt_dt?: string | null;
  uc_no_tx: string | null;
  /** 'RA' = Running / Part bill, 'FINAL' = Final bill. */
  bill_typ_cd?: 'RA' | 'FINAL' | null;
  rmrks_tx: string | null;
  i_ts?: string;
  // Read-only / join fields for display:
  wrk_cd?: string;
  prjct_id_tx?: string;
  wrk_nm?: string;
  prjct_ttl_tx?: string;
  ulb_nm?: string;
  dstrt_nm?: string;
  schme_cd?: string;
  schme_nm?: string;
  sls_cd?: string;
  fr_capex_totl_at?: number;
  pc_capex_totl_at?: number;
  bal_capex?: number;
  bal_unutlzd_at?: number;
  attch_n?: number;
}

/**
 * Abstract funding pattern for one SLS code — the sum of every approved project
 * cost carrying that code. Read-only: it is derived from the project entries,
 * never typed in.
 */
export interface BudgetAbstract {
  sls_cd: string;
  ctgry_tx?: string | null;
  sls_cmpnt_id: number | null;
  cmpnt_nm?: string | null;
  fndng_ptrn_tx?: string | null;
  prjct_n: number;
  capex_goi_at: number;
  capex_goap_at: number;
  capex_ulb_at: number;
  capex_15fc_at: number;
  capex_ppp_at: number;
  capex_totl_at: number;
  opex_ulb_at: number;
  totl_prjct_cst_at: number;
  fr_capex_totl_at: number;
  /** AIIB loan share (₹ rupees) — scheme-level rows only. */
  capex_aiib_at?: number;
  /** Others fund of the projects (₹ rupees) — e.g. Soft Loan. */
  othr_fnd_at?: number;
  /** 'SCHEME' for a non-AMRUT scheme's abstract (keyed scheme::<schme_id>); absent for SLS-code rows. */
  abs_scope?: 'SCHEME';
  schme_id?: number | null;
  /** Earliest project date under this SLS code — the floor for sanction/release dates. */
  min_prjct_dt?: string | null;
}

export const budgetEntryApi = {
  async getSlsComponents(): Promise<SlsComponent[]> {
    const response = await axiosInstance.get(`${BASE_URL}/budget-entry/sls-components`);
    return response.data.data || [];
  },

  /** Abstract funding pattern per SLS code and category. */
  async getAbstract(): Promise<Record<string, BudgetAbstract>> {
    const response = await axiosInstance.get(`${BASE_URL}/budget-entry/abstract`);
    const out: Record<string, BudgetAbstract> = {};
    (response.data.data || []).forEach((r: BudgetAbstract) => {
      // A scheme without SLS codes (AIIB, UIDF, …) — its abstract is keyed by scheme.
      if (r.abs_scope === 'SCHEME') {
        out[`scheme::${r.schme_id}`] = r;
        return;
      }
      const slsCd = (r.sls_cd || '').trim();
      const ctgry = (r.ctgry_tx || r.cmpnt_nm || '').trim();

      // Key by sls_cd alone (default/first fallback)
      if (!out[slsCd]) {
        out[slsCd] = r;
      }
      // Key by sls_cd + category
      if (ctgry) {
        out[`${slsCd}::${ctgry}`] = r;
        out[`${slsCd}::${ctgry.toLowerCase()}`] = r;
      }
      // Key by sls_cmpnt_id
      if (r.sls_cmpnt_id != null) {
        out[String(r.sls_cmpnt_id)] = r;
      }
    });
    return out;
  },

  async list(): Promise<BudgetEntry[]> {
    const response = await axiosInstance.get(`${BASE_URL}/budget-entry`);
    return response.data.data || [];
  },

  async create(data: BudgetEntryInput): Promise<BudgetEntry> {
    const response = await axiosInstance.post(`${BASE_URL}/budget-entry`, data);
    return response.data.data;
  },

  async update(id: number, data: BudgetEntryInput): Promise<BudgetEntry> {
    const response = await axiosInstance.put(`${BASE_URL}/budget-entry/${id}`, data);
    return response.data.data;
  },

  async remove(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/budget-entry/${id}`);
  },

  /** Dated history (Mother Sanctions + State releases) of one entry, oldest first. */
  async listTxns(id: number): Promise<BudgetTxn[]> {
    const response = await axiosInstance.get(`${BASE_URL}/budget-entry/${id}/txns`);
    return response.data.data || [];
  },

  async createTxn(id: number, data: BudgetTxnInput): Promise<BudgetTxn> {
    const response = await axiosInstance.post(`${BASE_URL}/budget-entry/${id}/txns`, data);
    return response.data.data;
  },

  async updateTxn(txnId: number, data: BudgetTxnInput): Promise<BudgetTxn> {
    const response = await axiosInstance.put(`${BASE_URL}/budget-entry/txns/${txnId}`, data);
    return response.data.data;
  },
};

/**
 * Work Packages API
 */
export const workPackagesApi = {
  async getAll(filters?: { search?: string; sts_cd?: string }, page = 1, limit = 50) {
    const params = { ...filters, page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/work-packages`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(id: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-packages/${id}`);
    return response.data.data;
  },

  // Period-wise progress for a package: target-vs-achieved broken down by
  // milestone / monthly / weekly, used by the Works Analysis dashboard bars.
  async getProgressTrend(id: string | number, granularity: 'milestone' | 'monthly' | 'weekly') {
    const response = await axiosInstance.get(`${BASE_URL}/work-packages/${id}/progress-trend`, { params: { granularity } });
    return response.data.data as {
      granularity: string;
      physical: Array<{ label: string; target: number; achieved: number }>;
      financial: Array<{ label: string; target: number; achieved: number }>;
    };
  },

  async create(data: { wrk_pkge_nm: string; dscn_tx?: string; work_ids: number[] }) {
    const response = await axiosInstance.post(`${BASE_URL}/work-packages`, data);
    return response.data.data;
  },

  async update(id: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/work-packages/${id}`, data);
    return response.data.data;
  },

  async delete(id: string | number) {
    await axiosInstance.delete(`${BASE_URL}/work-packages/${id}`);
  }
};

/**
 * Tender Sections API — section templates and per-tender document generation
 */
export const tenderSectionsApi = {
  async getMasters(tenderTypeCd?: string) {
    const params = tenderTypeCd ? { tenderTypeCd } : {};
    const response = await axiosInstance.get(`${BASE_URL}/tender-sections/masters`, { params });
    return response.data.data;
  },

  async getMasterById(id: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tender-sections/masters/${id}`);
    return response.data.data;
  },

  async createMaster(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-sections/masters`, data);
    return response.data.data;
  },

  async updateMaster(id: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/tender-sections/masters/${id}`, data);
    return response.data.data;
  },

  async createNewVersion(id: string | number, overrides?: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-sections/masters/${id}/version`, overrides || {});
    return response.data.data;
  },

  async getSectionVersions(sectionCd: string) {
    const response = await axiosInstance.get(`${BASE_URL}/tender-sections/versions/${sectionCd}`);
    return response.data.data;
  },

  async getTenderSections(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/sections`);
    return response.data.data;
  },

  async saveTenderSections(tenderId: string | number, sections: Record<string, any>[]) {
    const response = await axiosInstance.post(`${BASE_URL}/tenders/${tenderId}/sections`, { sections });
    return response.data.data;
  },

  async generateDocument(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/generate-document`);
    return response.data.data;
  },

  async autoPopulateVariables(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/auto-populate`);
    return response.data.data;
  }
};

/**
 * Tender Execution API - Bid evaluation, award & agreement
 */
export const tenderExecutionApi = {
  /** Server-paged. `listParams` carries the grid's search / filters / sort (toListParams). */
  async getAll(filters?: WMSFilters & { executionStatus?: string }, page = 1, limit = 50, listParams: Record<string, any> = {}) {
    const params: Record<string, any> = { ...buildFilterParams(filters), ...listParams, page, limit };
    if (filters?.executionStatus) params.executionStatus = filters.executionStatus;
    const response = await axiosInstance.get(`${BASE_URL}/tender-execution`, { params });
    return { data: response.data.data, pagination: response.data.pagination, summary: response.data.summary };
  },

  /** Values for one column's filter dropdown, from every tender in scope. */
  async getFilterValues(field: string, search = '', scope: Record<string, any> = {}): Promise<string[]> {
    const response = await axiosInstance.get(`${BASE_URL}/tender-execution/filter-values/${encodeURIComponent(field)}`, {
      params: { ...scope, search },
    });
    return response.data.data || [];
  },

  async getById(executionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tender-execution/${executionId}`);
    return response.data.data;
  },

  async evaluate(executionId: string | number, evaluationData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-execution/${executionId}/evaluate`, evaluationData);
    return response.data.data;
  },

  async approve(executionId: string | number, approvalData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-execution/${executionId}/approve`, approvalData);
    return response.data.data;
  },

  async issueLoA(executionId: string | number, loaData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-execution/${executionId}/issue-loa`, loaData);
    return response.data.data;
  },

  async signAgreement(executionId: string | number, agreementData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-execution/${executionId}/sign-agreement`, agreementData);
    return response.data.data;
  },

  async cancel(executionId: string | number, cancellationData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-execution/${executionId}/cancel`, cancellationData);
    return response.data.data;
  },

  async reTender(executionId: string | number, reTenderData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/tender-execution/${executionId}/re-tender`, reTenderData);
    return response.data.data;
  },

  async getBidComparison(executionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tender-execution/${executionId}/bid-comparison`);
    return response.data.data;
  },

  async getByTender(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/execution`);
    return response.data.data;
  }
};

/**
 * Award & Agreement API - Awards, LOA & Agreement management
 */
export const awardAgreementApi = {
  async getAll(filters?: WMSFilters & { awardStatus?: string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/award-agreement`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(awardId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/award-agreement/${awardId}`);
    return response.data.data;
  },

  /* ------------------------------------------------------------------
   * REMOVED (remediation Phase 7): 12 phantom write methods.
   *
   * create, update, approve, draftLOA, issueLOA, acceptLOA, draftAgreement,
   * signAgreement, recordSecurityDeposit, recordPerformanceGuarantee, startWork
   * and cancel all POSTed to /award-agreement/... routes that DO NOT EXIST on the
   * server — it registers only GET /award-agreement and GET /award-agreement/:id.
   * Every one fell through to the apiv1 catch-all and returned HTTP 702, so the
   * Work Order and LOA screens could never save anything.
   *
   * They are deleted rather than implemented because each duplicates a real
   * endpoint that already works:
   *   draft/sign agreement, security deposit, performance guarantee, start work
   *                            -> contractsApi.create / .update  (POST/PUT /contracts)
   *   draft/issue/accept LOA   -> tendersApi.createLOA / .updateLOA (POST/PUT /tenders/loa)
   * Building a second set would split contract and LOA writes across two code paths
   * with different validation — and the /contracts path is the one carrying the
   * award gate, the BOQ copy and the work advancement.
   * ---------------------------------------------------------------- */

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/award-agreement`);
    return response.data.data;
  },

  async getByTender(tenderId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/tenders/${tenderId}/award-agreement`);
    return response.data.data;
  }
};

/**
 * Contractors API
 */
export const contractorsApi = {
  /** Server-paged. `filters` may also carry the grid's params from toListParams (filters JSON, sortBy, sortDir). */
  async getAll(filters?: { categoryId?: number; status?: string; search?: string; pwdClass?: string; [k: string]: any }, page = 1, limit = 50) {
    const params = { ...filters, page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/contractors`, { params });
    return { data: response.data.data, pagination: response.data.pagination, summary: response.data.summary };
  },

  /**
   * Every contractor in scope for a dropdown: { cntrctr_id, cntrctr_cd, cntrctr_nm,
   * frm_nm, cntrctr_sts_cd, phn_nu }. Not paged — the list endpoint (getAll) is paged
   * at 20, so select boxes read this instead of asking getAll for hundreds of rows.
   */
  async getOptions(scope: Record<string, any> = {}): Promise<{ data: any[] }> {
    const response = await axiosInstance.get(`${BASE_URL}/contractors/options`, { params: scope });
    return { data: response.data.data || [] };
  },

  /** Values for one column's filter dropdown, from every contractor in scope. */
  async getFilterValues(field: string, search = '', scope: Record<string, any> = {}): Promise<string[]> {
    const response = await axiosInstance.get(`${BASE_URL}/contractors/filter-values/${encodeURIComponent(field)}`, {
      params: { ...scope, search },
    });
    return response.data.data || [];
  },

  async getById(contractorId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contractors/${contractorId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/contractors`, data);
    return response.data.data;
  },

  async update(contractorId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/contractors/${contractorId}`, data);
    return response.data.data;
  },

  async delete(contractorId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/contractors/${contractorId}`);
  },

  async getWorks(contractorId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contractors/${contractorId}/works`);
    return response.data.data;
  },

  async getCategories() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/contractor-categories`);
    return response.data.data;
  },

  // Contractor Items (Services/Machinery/Goods)
  async getItems(contractorId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contractors/${contractorId}/items`);
    return response.data.data;
  },

  async saveItems(contractorId: string | number, items: any[]) {
    const response = await axiosInstance.post(`${BASE_URL}/contractors/${contractorId}/items/bulk`, { items });
    return response.data.data;
  },

  async addItem(contractorId: string | number, item: any) {
    const response = await axiosInstance.post(`${BASE_URL}/contractors/${contractorId}/items`, item);
    return response.data.data;
  },

  async updateItem(contractorId: string | number, itemId: string | number, item: any) {
    const response = await axiosInstance.put(`${BASE_URL}/contractors/${contractorId}/items/${itemId}`, item);
    return response.data.data;
  },

  async deleteItem(contractorId: string | number, itemId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/contractors/${contractorId}/items/${itemId}`);
  },

  // Get SOR items for picker
  async getSORItems(search?: string, category?: string) {
    const params: any = {};
    if (search) params.search = search;
    if (category) params.category = category;
    const response = await axiosInstance.get(`${BASE_URL}/sor-items/picker`, { params });
    return response.data.data;
  },

  // Contractor Contacts
  async getContacts(contractorId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contractors/${contractorId}/contacts`);
    return response.data.data;
  },

  async saveContacts(contractorId: string | number, contacts: any[]) {
    const response = await axiosInstance.post(`${BASE_URL}/contractors/${contractorId}/contacts/bulk`, { contacts });
    return response.data.data;
  },

  async addContact(contractorId: string | number, contact: any) {
    const response = await axiosInstance.post(`${BASE_URL}/contractors/${contractorId}/contacts`, contact);
    return response.data.data;
  },

  async updateContact(contractorId: string | number, contactId: string | number, contact: any) {
    const response = await axiosInstance.put(`${BASE_URL}/contractors/${contractorId}/contacts/${contactId}`, contact);
    return response.data.data;
  },

  async deleteContact(contractorId: string | number, contactId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/contractors/${contractorId}/contacts/${contactId}`);
  },

  // Contractor Documents
  async getDocuments(contractorId: number) {
    const response = await axiosInstance.get(`${BASE_URL}/contractors/${contractorId}/documents`);
    return response.data.data;
  },

  async uploadDocument(contractorId: number, formData: FormData) {
    const response = await axiosInstance.post(
      `${BASE_URL}/contractors/${contractorId}/documents`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  async updateDocument(docId: number, data: any) {
    const response = await axiosInstance.put(`${BASE_URL}/documents/${docId}`, data);
    return response.data.data;
  },

  async deleteDocument(docId: number) {
    const response = await axiosInstance.delete(`${BASE_URL}/documents/${docId}`);
    return response.data;
  }
};

// ============================================================
// Document Config Types & API
// ============================================================

export interface CustomFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

export interface DocumentTypeConfig {
  doc_type_cnfg_id: number;
  doc_type_cd: string;
  doc_type_nm: string;
  dscn_tx: string | null;
  icon_cd: string | null;
  is_rqrd_in: boolean;
  accptd_frmts_tx: string;
  max_file_sz_nu: number;
  multi_file_in: boolean;
  max_files_nu: number;
  min_files_nu: number;
  cstm_flds_jn: CustomFieldConfig[];
  sqnce_nu: number;
}

export interface DocumentProcessConfig {
  doc_prcss_id: number;
  app_id: string;
  hndlr_cd: string;
  prcss_nm: string;
  entity_type_cd: string;
  dscn_tx: string | null;
  icon_cd: string | null;
  sqnce_nu: number;
  docTypes: DocumentTypeConfig[];
}

export interface DocumentUpload {
  doc_upld_id: number;
  app_id: string;
  doc_prcss_id: number;
  doc_type_cnfg_id: number;
  ref_id: number;
  ref_nm: string;
  doc_nm: string | null;
  doc_nu: string | null;
  file_nm: string;
  orgnl_file_nm: string | null;
  file_url: string | null;
  file_sz: number | null;
  cntnt_type: string | null;
  issue_dt: string | null;
  expiry_dt: string | null;
  rmrks_tx: string | null;
  cstm_data_jn: Record<string, any>;
  sqnce_nu: number;
  doc_type_cd: string;
  doc_type_nm: string;
  i_ts: string;
  u_ts: string;
}

export interface WorkDocGroup {
  hndlr_cd: string;
  prcss_nm: string;
  entity_type_cd: string;
  icon_cd: string;
  sqnce_nu: number;
  docs: DocumentUpload[];
}

export const documentConfigApi = {
  async getConfig(handlerCd: string): Promise<DocumentProcessConfig> {
    const response = await axiosInstance.get<ApiResponse<DocumentProcessConfig>>(`${BASE_URL}/document-config/${handlerCd}`);
    return response.data.data;
  },

  async getAllProcesses(): Promise<any[]> {
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/document-config`);
    return response.data.data;
  },

  async getDocuments(handlerCd: string, refId: number): Promise<DocumentUpload[]> {
    const response = await axiosInstance.get<ApiResponse<DocumentUpload[]>>(`${BASE_URL}/documents/by-process/${handlerCd}/${refId}`);
    return response.data.data;
  },

  async uploadDocument(formData: FormData): Promise<DocumentUpload> {
    const response = await axiosInstance.post<ApiResponse<DocumentUpload>>(
      `${BASE_URL}/documents/upload`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  async updateDocument(docUpldId: number, data: Record<string, any>): Promise<DocumentUpload> {
    const response = await axiosInstance.put<ApiResponse<DocumentUpload>>(`${BASE_URL}/documents/${docUpldId}`, data);
    return response.data.data;
  },

  async deleteDocument(docUpldId: number): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/documents/${docUpldId}`);
  },

  async getWorkAllDocs(workId: number, tenderId?: number): Promise<WorkDocGroup[]> {
    const params = tenderId ? `?tenderId=${tenderId}` : '';
    const response = await axiosInstance.get<ApiResponse<WorkDocGroup[]>>(`${BASE_URL}/documents/work-all/${workId}${params}`);
    return response.data.data;
  }
};

/**
 * Contracts API
 */
export const contractsApi = {
  async getAll(filters?: WMSFilters, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/contracts`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(contractId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contracts/${contractId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/contracts`, data);
    return response.data.data;
  },

  async update(contractId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/contracts/${contractId}`, data);
    return response.data.data;
  },

  async delete(contractId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/contracts/${contractId}`);
  },

  async getBOQ(contractId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contracts/${contractId}/boq`);
    return response.data.data;
  },

  /**
   * Contracts awarded against one work. GET /contracts already supports a workId
   * filter, but buildFilterParams has no workId key so getAll silently drops it —
   * hence the explicit params here rather than a filters object.
   *
   * Quality tests and NCRs hang off the CONTRACT, not the work, so the work profile
   * needs the contract before it can show either.
   */
  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contracts`, {
      params: { workId, page: 1, limit: 50 },
    });
    return response.data.data || [];
  }
};

/**
 * Payments API
 */
export const paymentsApi = {
  async getAll(filters?: WMSFilters & { billType?: string; paymentStatus?: string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/payments`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(paymentId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/payments/${paymentId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/payments`, data);
    return response.data.data;
  },

  async update(paymentId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/payments/${paymentId}`, data);
    return response.data.data;
  },

  async delete(paymentId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/payments/${paymentId}`);
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/payments`);
    return response.data.data;
  },

  async getByContract(contractId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contracts/${contractId}/payments`);
    return response.data.data;
  },

  async getBillTypes() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/bill-types`);
    return response.data.data;
  },

  async getDeductions(paymentId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/payments/${paymentId}/deductions`);
    return response.data.data;
  },

  /* Every stage a bill has been through. The bill row stamps a date per rung but
   * never a name — who submitted, checked, passed or approved it lives only in the
   * task trail, which is what this returns, in the shape WorkflowHistoryDialog
   * already renders for proposals, estimates and measurement books. */
  async getBillWorkflowHistory(billId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/bills/${billId}/workflow-history`);
    return response.data.data;
  }
};

/**
 * Physical Progress API - Measurements
 */
export const measurementsApi = {
  // e-MB lifecycle. These four endpoints have existed on the server since the module
  // was ported (POST /emb/:id/{submit,check,approve,reject}) but nothing ever called
  // them, so a Measurement Book could never leave DRAFT — and Check Measurements
  // excludes DRAFT, so nothing downstream could consume it either.
  async setEmbStatus(embId: number | string, action: 'submit' | 'check' | 'approve' | 'reject', data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/emb/${embId}/${action}`, data);
    return response.data.data;
  },

  /* Every stage this measurement book has been through — who it went to, who acted and
   * what they decided. Keyed as prpsl_* so WorkflowHistoryDialog renders it without
   * branching; the measured/checked/approved fields are the book's own certification
   * stamps, which that dialog shows as a strip above the trail. */
  async getWorkflowHistory(embId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/emb/${embId}/workflow-history`);
    return response.data.data as {
      prpsl_id: number; prpsl_cd: string; prpsl_nm: string; prpsl_sts_cd: string;
      created: { at: string; by: string | null };
      measured_by: string | null; measured_by_dsgn: string | null; submitted_at: string | null;
      checked_by: string | null; checked_at: string | null; checked_remarks: string | null;
      approved_by: string | null; approved_at: string | null;
      entries: WorkflowHistoryEntry[];
    };
  },

  // Officers this measurement book can be sent to at a stage. Same shape as the proposal
  // and estimate versions, so SubmitToOfficerDialog renders it without branching — the
  // measurement book was the only WMS document still advanced without naming a recipient,
  // so its task landed on every holder of the position rather than one officer's desk.
  async getWorkflowAssignees(embId: string | number, stage = 'SUBMITTED') {
    const response = await axiosInstance.get(
      `${BASE_URL}/emb/${embId}/workflow-assignees`, { params: { stage } });
    return response.data.data as {
      emb_cd: string; mbook_nu: string; stage: string; uda_id: number | null;
      configured: boolean; nxt_actn_tx: string | null;
      designation: { id: number; name: string; rank: number } | null;
      default: WorkflowAssignee | null;
      candidates: WorkflowAssignee[];
      /* The whole assignable directory for this record's authority, as
       * Department -> Designation -> Employee. `candidates` above is the routing
       * matrix's answer and stays the default; this is the browse path, and it
       * carries the posts the rank ladder cannot score. */
      tree: WorkflowAssigneeDept[];
    };
  },

  async getAll(filters?: WMSFilters & { measurementType?: string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/measurements`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(measurementId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/measurements/${measurementId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/measurements`, data);
    return response.data.data;
  },

  async update(measurementId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/measurements/${measurementId}`, data);
    return response.data.data;
  },

  async delete(measurementId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/measurements/${measurementId}`);
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/measurements`);
    return response.data.data;
  },

  async getItems(measurementId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/measurements/${measurementId}/items`);
    return response.data.data;
  },

  async addItem(measurementId: string | number, itemData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/measurements/${measurementId}/items`, itemData);
    return response.data.data;
  }
};

/**
 * Check Measurements API
 */
export const checkMeasurementsApi = {
  async getAll(filters?: WMSFilters, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/check-measurements`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(checkMeasurementId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/check-measurements/${checkMeasurementId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/check-measurements`, data);
    return response.data.data;
  },

  async update(checkMeasurementId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/check-measurements/${checkMeasurementId}`, data);
    return response.data.data;
  },

  async approve(checkMeasurementId: string | number, approvalData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/check-measurements/${checkMeasurementId}/approve`, approvalData);
    return response.data.data;
  },

  async reject(checkMeasurementId: string | number, rejectionData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/check-measurements/${checkMeasurementId}/reject`, rejectionData);
    return response.data.data;
  },

  async getByMeasurement(measurementId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/measurements/${measurementId}/check-measurements`);
    return response.data.data;
  }
};

/**
 * Quality Control (QC) API
 */
export const qcApi = {
  async getAll(filters?: WMSFilters & { testType?: string; testResult?: string; status?: string; search?: string }, page = 1, limit = 50) {
    const params: Record<string, any> = { ...buildFilterParams(filters), page, limit };
    if (filters?.testType) params.testType = filters.testType;
    if (filters?.testResult) params.testResult = filters.testResult;
    // Pass / Fail KPI card — kept apart from the Result filter.
    if ((filters as any)?.kpiResult) params.kpiResult = (filters as any).kpiResult;
    if ((filters as any)?.status) params.status = (filters as any).status;
    if ((filters as any)?.search) params.search = (filters as any).search;
    const response = await axiosInstance.get(`${BASE_URL}/quality-control`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  // The page filters (Test Type, Result, Search) narrow the KPI counts like the list.
  async getSummary(filters?: { testType?: string; testResult?: string; search?: string }) {
    const params: Record<string, any> = {};
    if (filters?.testType) params.testType = filters.testType;
    if (filters?.testResult) params.testResult = filters.testResult;
    if (filters?.search) params.search = filters.search;
    const response = await axiosInstance.get(`${BASE_URL}/quality-control/summary`, { params });
    return response.data.data;
  },

  async getById(qcId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/quality-control/${qcId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/quality-control`, data);
    return response.data.data;
  },

  async update(qcId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/quality-control/${qcId}`, data);
    return response.data.data;
  },

  async delete(qcId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/quality-control/${qcId}`);
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/quality-control`);
    return response.data.data;
  },

  // activeOnly is optional and defaults to true so existing QC callers are unchanged.
  // The Quality Test Types master passes false: MasterManagement soft-deletes by
  // setting a_in = false, and a list that only fetched active rows would make a
  // deleted type disappear with no way to restore it.
  async getTestTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/qc-test-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getTestResults(qcId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/quality-control/${qcId}/results`);
    return response.data.data;
  },

  async addTestResult(qcId: string | number, resultData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/quality-control/${qcId}/results`, resultData);
    return response.data.data;
  }
};

/**
 * Non-Conformance Reports (NCR) API
 */
export const ncrApi = {
  async getAll(filters?: { status?: string; severity?: string; cntrct_id?: string | number; search?: string }, page = 1, limit = 50) {
    const params: Record<string, any> = { ...filters, page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/ncr`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(ncrId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/ncr/${ncrId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/ncr`, data);
    return response.data.data;
  },

  async update(ncrId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/ncr/${ncrId}`, data);
    return response.data.data;
  },

  async getSeverities() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/ncr-severities`);
    return response.data.data;
  },

  async getDispositions() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/ncr-dispositions`);
    return response.data.data;
  }
};

/**
 * Inspections API
 */
export const inspectionsApi = {
  async getAll(filters?: WMSFilters & { inspectionType?: string; status?: string; critical?: boolean; drill?: string }, page = 1, limit = 50) {
    // status / inspectionType / critical are this endpoint's own params — buildFilterParams
    // knows none of them, so they were silently dropped and the dropdowns did nothing.
    const params: Record<string, any> = { ...buildFilterParams(filters), page, limit };
    if (filters?.status) params.status = filters.status;
    if (filters?.inspectionType) params.inspectionType = filters.inspectionType;
    if (filters?.critical) params.critical = 1;
    // The KPI card, kept apart from the Status filter (see the /inspections route).
    if (filters?.drill && filters.drill !== 'all') params.drill = filters.drill;
    const response = await axiosInstance.get(`${BASE_URL}/inspections`, { params });
    return { data: response.data.data, pagination: response.data.pagination, summary: response.data.summary };
  },

  async getById(inspectionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/inspections/${inspectionId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/inspections`, data);
    return response.data.data;
  },

  async update(inspectionId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/inspections/${inspectionId}`, data);
    return response.data.data;
  },

  async delete(inspectionId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/inspections/${inspectionId}`);
  },

  /**
   * The full history of one inspection: the record, a chronological timeline and
   * the site photos, in one call.
   *
   * getById returns the row as it stands now — the outcome, but not how it got
   * there. This adds who scheduled it, which officer it was handed to and when,
   * every photograph with its capture time and GPS point, and who recorded the
   * result. The server assembles it from the inspection's own audit stamps, the
   * assignment notifications and the geo-stamped photos; there is no separate
   * history table, so an inspection edited twice shows one "recorded" event
   * carrying the CURRENT outcome rather than each intermediate value.
   */
  async getHistory(inspectionId: string | number): Promise<InspectionHistory> {
    const response = await axiosInstance.get<ApiResponse<InspectionHistory>>(
      `${BASE_URL}/inspections/${inspectionId}/history`);
    return response.data.data;
  },

  /** Just the photographs captured for one inspection, whoever took them. */
  async getPhotos(inspectionId: string | number): Promise<InspectionPhoto[]> {
    const response = await axiosInstance.get<ApiResponse<InspectionPhoto[]>>(
      `${BASE_URL}/inspections/${inspectionId}/photos`);
    return response.data.data || [];
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/inspections`);
    return response.data.data;
  },

  async getInspectionTypes() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/inspection-types`);
    return response.data.data;
  },

  async getFindings(inspectionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/inspections/${inspectionId}/findings`);
    return response.data.data;
  },

  async addFinding(inspectionId: string | number, findingData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/inspections/${inspectionId}/findings`, findingData);
    return response.data.data;
  },

  async schedule(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/inspections/schedule`, data);
    return response.data.data;
  },

  /**
   * Officers who can be named as the inspector, out of the employee master.
   *
   * Pass the work being inspected: the list is then the work's own authority, which is
   * what an inspection belongs to — the header UDA is only the fallback, and in the
   * state-level "All UDAs" view there is no header UDA at all.
   *
   * Work Inspectors sort first; engineers and planning officers follow, because only
   * three of the twenty-one authorities have a Work Inspector on the rolls.
   */
  async getInspectorCandidates(wrkId?: string | number): Promise<InspectorCandidatesResponse> {
    const response = await axiosInstance.get<ApiResponse<InspectorCandidatesResponse>>(
      `${BASE_URL}/inspections/inspectors`, { params: wrkId ? { wrkId } : {} });
    return response.data.data;
  }
};

/** One site photograph captured for an inspection. */
export interface InspectionPhoto {
  cmpnt_img_id: number;
  attchmnt_id: number;
  file_nm: string | null;
  cntnt_type: string | null;
  file_sz: number | null;
  /** The caption the inspector typed, with the storage marker already stripped. */
  cptn_tx: string | null;
  lat: number | null;
  lng: number | null;
  addr_tx: string | null;
  accuracy: number | null;
  geo_type: string | null;
  /** CAMERA for a photo shot in the field, null for one picked from the gallery. */
  src_cd: string | null;
  captured_at: string | null;
  captured_at_tx: string | null;
  uploaded_at: string;
  uploaded_by_id: number | null;
  uploaded_by_nm: string | null;
  /** Server path for the bytes — pass attchmnt_id to attachmentApi.fetchBlobUrl to render it. */
  url: string;
}

/** One dated event in an inspection's history. */
export interface InspectionEvent {
  event_cd: 'SCHEDULED' | 'ASSIGNED' | 'REASSIGNED' | 'PHOTO' | 'RECORDED' | 'COMPLETED';
  event_nm: string;
  event_ts: string;
  event_ts_tx: string | null;
  actor_id: number | null;
  actor_nm: string | null;
  actor_dsgn_nm: string | null;
  dtl_tx: string | null;
  /** PHOTO events only. */
  attchmnt_id?: number;
  url?: string;
  lat?: number | null;
  lng?: number | null;
  addr_tx?: string | null;
  src_cd?: string | null;
  /** RECORDED / COMPLETED events only — the observations written on site. */
  rmrks_tx?: string | null;
  lctn_tx?: string | null;
}

export interface InspectionHistory {
  inspection: Record<string, any> & {
    inspctn_id: number;
    inspctn_cd: string;
    schdld_by_nm: string | null;
    schdld_by_dsgn_nm: string | null;
    rcrdd_by_nm: string | null;
    /** Set only when the inspection is actually COMPLETED. */
    cmpltd_by_nm: string | null;
    photo_ct: number;
  };
  timeline: InspectionEvent[];
  photos: InspectionPhoto[];
}

/** One officer who can be named as an inspector. */
export interface InspectorCandidate {
  employee_id: number;
  employee_nm: string;
  employee_cd: string | null;
  mobilenumber: string | null;
  designation_id: number;
  designation_nm: string;
  uda_id: number | null;
  /** 1 = Work Inspector, 2 = other inspector, 3 = engineer, 4 = planning/technical. */
  rnk: number;
}

export interface InspectorCandidatesResponse {
  uda_ids: number[] | null;
  designations: { designation_id: number; designation_nm: string; rnk: number; emp_ct: number }[];
  candidates: InspectorCandidate[];
}

/** One stage in a proposal's workflow trail. */
export interface WorkflowHistoryEntry {
  id: number;
  stage: string;
  status: string;
  /** Null while still PENDING; otherwise APPROVED / REJECTED / RETURNED / SUPERSEDED. */
  outcome: string | null;
  owner: string | null;
  ownerDesignation: string | null;
  initiatedBy: string | null;
  actedBy: string | null;
  raisedAt: string;
  actedAt: string | null;
  nextAction: string | null;
  remarks: string | null;
  fromStatus: string | null;
  toStatus: string | null;
}

/** One officer who can receive a proposal at a workflow stage. */
/**
 * One department in the assignable directory, with the designations actually filled in
 * it and the people holding them — the Department -> Designation -> Employee cascade.
 *
 * Built server-side from live postings (assigneeTree in wf-dispatch.utils), so every
 * branch ends in at least one currently-posted person and no choice is a dead end.
 * Structurally identical to AssigneePicker's own AssigneeDepartment, which is what the
 * component consumes; declared here so the API's shape is stated where the API is.
 */
export interface WorkflowAssigneeDept {
  departmentid: number;
  department_nm: string;
  designations: {
    designationid: number;
    designation_nm: string;
    employees: {
      employeeid: number;
      positionid: number | null;
      employee_nm: string | null;
      mobilenumber?: string | null;
    }[];
  }[];
}

export interface WorkflowAssignee {
  employeeid: number;
  positionid: number;
  designationid: number;
  designation_nm: string;
  employee_nm: string | null;
  mobilenumber: string | null;
  rnk: number;
  /** True when this officer actually holds the configured post, false for a senior fallback. */
  is_exact: boolean;
}

/**
 * Proposals API - Work proposals management
 */
export const proposalsApi = {
  /**
   * Server-paged. `listParams` carries the page's card filters (proposalStatus,
   * proposalType, schemeName, sourceName, workTypeName, agingMin, agingMax), the grid's
   * search / filters / sort (toListParams) and summary: 1 for the cards' figures.
   */
  async getAll(filters?: WMSFilters & { proposalStatus?: string; proposalType?: string }, page = 1, limit = 50, listParams: Record<string, any> = {}) {
    const params = { ...buildFilterParams(filters), ...listParams, page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/proposals`, { params });
    return { data: response.data.data, pagination: response.data.pagination, summary: response.data.summary };
  },

  /** Values for one column's filter dropdown, from every proposal in scope. */
  async getFilterValues(field: string, search = '', scope: Record<string, any> = {}): Promise<string[]> {
    const response = await axiosInstance.get(`${BASE_URL}/proposals/filter-values/${encodeURIComponent(field)}`, {
      params: { ...scope, search },
    });
    return response.data.data || [];
  },

  async getById(proposalId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/proposals/${proposalId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals`, data);
    return response.data.data;
  },

  async update(proposalId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/proposals/${proposalId}`, data);
    return response.data.data;
  },

  async delete(proposalId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/proposals/${proposalId}`);
  },

  // `data` may carry asgn_usr_id / asgn_pos_id / asgn_dsgn_id — the officer picked in the
  // Submit dialog. Omit them and the server routes to the configured designation for the
  // proposal's own UDA instead.
  async submit(proposalId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/submit`, data);
    return response.data.data;
  },

  // Who this proposal can be submitted to: the designation the matrix routes this stage
  // to in the proposal's OWN authority, the officers holding it (plus seniors, since the
  // post is vacant in most UDAs), and the default the engine would pick unaided.
  // Every stage this proposal has passed through: who held it, who acted, the outcome.
  async getWorkflowHistory(proposalId: string | number) {
    const response = await axiosInstance.get(
      `${BASE_URL}/proposals/${proposalId}/workflow-history`);
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; prpsl_sts_cd: string;
      created: { at: string; by: string | null };
      entries: WorkflowHistoryEntry[];
    };
  },

  async getWorkflowAssignees(proposalId: string | number, stage = 'SUBMITTED') {
    const response = await axiosInstance.get(
      `${BASE_URL}/proposals/${proposalId}/workflow-assignees`, { params: { stage } });
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; stage: string; uda_id: number | null;
      configured: boolean; nxt_actn_tx: string | null;
      designation: { id: number; name: string; rank: number } | null;
      default: WorkflowAssignee | null;
      candidates: WorkflowAssignee[];
      /* The whole assignable directory for this record's authority, as
       * Department -> Designation -> Employee. `candidates` above is the routing
       * matrix's answer and stays the default; this is the browse path, and it
       * carries the posts the rank ladder cannot score. */
      tree: WorkflowAssigneeDept[];
    };
  },

  async approve(proposalId: string | number, approvalData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/approve`, approvalData);
    return response.data.data;
  },

  async reject(proposalId: string | number, rejectionData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/reject`, rejectionData);
    return response.data.data;
  },

  // Workflow transitions. The server fixes the target status per route and validates
  // it against the PROPOSAL transition map, so these cannot be used to jump a stage —
  // an out-of-order call returns 409 with the reason.
  async review(proposalId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/review`, data);
    return response.data.data;
  },

  async reopen(proposalId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/reopen`, data);
    return response.data.data;
  },

  async cancel(proposalId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/cancel`, data);
    return response.data.data;
  },

  // NOTE: there is no POST /proposals/:id/convert-to-work route on the server. The
  // real endpoint is createWork() below (/create-work), which the Proposals page
  // already uses. Kept only so any existing caller fails loudly rather than silently.
  async convertToWork(proposalId: string | number) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/convert-to-work`);
    return response.data.data;
  },

  // New methods for proposal-sanction-work relationships
  async getLinkedWorks(proposalId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/proposals/${proposalId}/works`);
    return response.data.data;
  },

  async linkToAdminSanction(proposalId: string | number, data: { adminSanctionId: number; prpsd_at?: number; snctn_at?: number; rmrks_tx?: string }) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/link-sanction`, data);
    return response.data.data;
  },

  async createWork(proposalId: string | number, workData?: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/create-work`, workData || {});
    return response.data.data;
  },

  async createWorks(proposalId: string | number, works: Array<Record<string, any>>) {
    const response = await axiosInstance.post(`${BASE_URL}/proposals/${proposalId}/create-works`, { works });
    return response.data.data;
  }
};

/**
 * Works List API — server-side aggregation, pagination, search for 20K+ works
 */
export const worksListApi = {
  async getAll(filters?: Record<string, any>, page = 1, limit = 50) {
    const params: Record<string, any> = { ...filters, page, limit };
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/works-list`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  // Dedicated Pushkaralu works list (event-tagged works, hidden from the default list).
  async getPushkaralu(filters?: Record<string, any>, page = 1, limit = 50) {
    const params: Record<string, any> = { ...filters, page, limit };
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/works-list/pushkaralu`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getSummary(groupBy: string, filters?: Record<string, any>) {
    const params: Record<string, any> = { ...filters, groupBy };
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/works-list/summary`, { params });
    return { data: response.data.data, groupLabel: (response.data as any).groupLabel };
  },

  async getStatusCounts(filters?: Record<string, any>) {
    const params: Record<string, any> = { ...filters };
    const response = await axiosInstance.get<ApiResponse<any[]>>(`${BASE_URL}/works-list/status-counts`, { params });
    return response.data.data;
  },

  async getFilterOptions() {
    const response = await axiosInstance.get<ApiResponse<any>>(`${BASE_URL}/works-list/filter-options`);
    return response.data.data;
  }
};

/**
 * Technical Sanctions API
 */
export const technicalSanctionsApi = {
  /* Officers who may sanction this TS.
   *
   * Resolved from the authority named ON THE RECORD (snctn_auth_id), not from the routing
   * matrix the other chains use — the form already states who is competent, and that
   * varies per work. `from_sanction_authority` tells the dialog to say so. */
  // Whether the caller may approve/reject this TS, and who holds it.
  async getWorkflowState(tsId: string | number) {
    const response = await axiosInstance.get(
      `${BASE_URL}/technical-sanctions/${tsId}/workflow-state`);
    return response.data.data as {
      ts_id: number; ts_sts_cd: string;
      can_act: boolean; pending_with_nm: string; is_creator: boolean;
    };
  },

  /* Officers holding a sanctioning authority, for a work with no TS yet.
   *
   * Fills the Sanctioning Officer dropdown ON THE FORM, at creation. getWorkflowAssignees
   * below answers the same question for a saved sanction; this one takes a work and an
   * authority because at creation there is no sanction id to ask about. */
  async getSanctioningOfficers(wrkId: number | string | null, snctnAuthId: number | string) {
    const response = await axiosInstance.get(
      `${BASE_URL}/technical-sanctions/sanctioning-officers`,
      { params: { wrkId: wrkId ?? undefined, snctnAuthId } });
    return response.data.data as {
      snctn_auth_id: number; snctn_auth_cd: string; snctn_auth_nm: string;
      uda_id: number | null;
      dsgn_id: number | null; dsgn_nm: string;
      candidates: WorkflowAssignee[];
      default: WorkflowAssignee | null;
    };
  },

  async getWorkflowAssignees(tsId: string | number, stage = 'SUBMITTED') {
    const response = await axiosInstance.get(
      `${BASE_URL}/technical-sanctions/${tsId}/workflow-assignees`, { params: { stage } });
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; stage: string; uda_id: number | null;
      configured: boolean; nxt_actn_tx: string | null;
      designation: { id: number; name: string; rank: number } | null;
      from_sanction_authority?: boolean; snctn_auth_cd?: string;
      default: WorkflowAssignee | null;
      /* True when the pre-selected officer is the one named on the sanction itself,
       * rather than a fallback picked because that officer no longer holds the post. */
      default_from_record?: boolean;
      candidates: WorkflowAssignee[];
      /* The whole assignable directory for this record's authority, as
       * Department -> Designation -> Employee. `candidates` above is the routing
       * matrix's answer and stays the default; this is the browse path, and it
       * carries the posts the rank ladder cannot score. */
      tree: WorkflowAssigneeDept[];
    };
  },

  async getAll(filters?: WMSFilters & { sanctionStatus?: string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/technical-sanctions`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(sanctionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/technical-sanctions/${sanctionId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/technical-sanctions`, data);
    return response.data.data;
  },

  async update(sanctionId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/technical-sanctions/${sanctionId}`, data);
    return response.data.data;
  },

  async submit(sanctionId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/technical-sanctions/${sanctionId}/submit`, data);
    return response.data.data;
  },

  async approve(sanctionId: string | number, approvalData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/technical-sanctions/${sanctionId}/approve`, approvalData);
    return response.data.data;
  },

  async reject(sanctionId: string | number, rejectionData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/technical-sanctions/${sanctionId}/reject`, rejectionData);
    return response.data.data;
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/technical-sanctions`);
    return response.data.data;
  },

  async getAuthorities() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sanction-authorities`, {
      params: { activeOnly: true, sanctionType: 'TECH' }
    });
    return response.data.data;
  }
};

/**
 * Admin Sanctions API
 */
export const adminSanctionsApi = {
  // Officers this sanction can be forwarded to for approval.
  async getWorkflowAssignees(asId: string | number, stage = 'SUBMITTED') {
    const response = await axiosInstance.get(
      `${BASE_URL}/admin-sanctions/${asId}/workflow-assignees`, { params: { stage } });
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; stage: string; uda_id: number | null;
      configured: boolean; nxt_actn_tx: string | null;
      designation: { id: number; name: string; rank: number } | null;
      default: WorkflowAssignee | null;
      candidates: WorkflowAssignee[];
      /* The whole assignable directory for this record's authority, as
       * Department -> Designation -> Employee. `candidates` above is the routing
       * matrix's answer and stays the default; this is the browse path, and it
       * carries the posts the rank ladder cannot score. */
      tree: WorkflowAssigneeDept[];
    };
  },

  // Stage trail for a sanction.
  async getWorkflowHistory(asId: string | number) {
    const response = await axiosInstance.get(
      `${BASE_URL}/admin-sanctions/${asId}/workflow-history`);
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; prpsl_sts_cd: string;
      created: { at: string; by: string | null };
      entries: WorkflowHistoryEntry[];
    };
  },

  // Status plus whether THIS caller may act, and who it is waiting on.
  async getWorkflowState(asId: string | number) {
    const response = await axiosInstance.get(
      `${BASE_URL}/admin-sanctions/${asId}/workflow-state`);
    return response.data.data as {
      as_id: number; as_cd: string; as_sts_cd: string;
      can_act: boolean; pending_with_nm: string; is_creator: boolean;
    };
  },

  async submitForApproval(asId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions/${asId}/submit`, data);
    return response.data;
  },

  async getAll(filters?: WMSFilters & { sanctionStatus?: string; status?: string }, page = 1, limit = 50) {
    // GET /admin-sanctions filters on `status` (admin-sanctions.controller.js). It is
    // not one of the keys buildFilterParams knows, so neither spelling ever left the
    // browser and every caller received the unfiltered list — including the Technical
    // Sanction "Import AA" picker, which asks for APPROVED only and was being offered
    // DRAFT sanctions to import from.
    const params: Record<string, any> = { ...buildFilterParams(filters), page, limit };
    const status = filters?.status ?? filters?.sanctionStatus;
    if (status) params.status = status;
    const response = await axiosInstance.get(`${BASE_URL}/admin-sanctions`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(sanctionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/admin-sanctions/${sanctionId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions`, data);
    return response.data.data;
  },

  async update(sanctionId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/admin-sanctions/${sanctionId}`, data);
    return response.data.data;
  },

  async submit(sanctionId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions/${sanctionId}/submit`, data);
    return response.data.data;
  },

  async approve(sanctionId: string | number, approvalData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions/${sanctionId}/approve`, approvalData);
    return response.data.data;
  },

  async reject(sanctionId: string | number, rejectionData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions/${sanctionId}/reject`, rejectionData);
    return response.data.data;
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/admin-sanctions`);
    return response.data.data;
  },

  async getAuthorities() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sanction-authorities`, {
      params: { activeOnly: true, sanctionType: 'ADMN' }
    });
    return response.data.data;
  },

  // New methods for proposal-sanction-work relationships
  async getLinkedProposals(sanctionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/admin-sanctions/${sanctionId}/proposals`);
    return response.data.data;
  },

  async getLinkedWorks(sanctionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/admin-sanctions/${sanctionId}/works`);
    return response.data.data;
  },

  async getWorksByGO(goNu: string, udaId?: number | null, fyId?: number | null) {
    const params: Record<string, any> = { go_nu: goNu };
    if (udaId) params.ulb_id = udaId;
    if (fyId) params.fy_id = fyId;
    const response = await axiosInstance.get(`${BASE_URL}/admin-sanctions/go-works`, { params });
    return response.data.data;
  },

  async linkProposals(sanctionId: string | number, proposals: Array<{ prpsl_id: number; prpsd_at?: number; snctn_at?: number; sqnce_nu?: number; rmrks_tx?: string }>) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions/${sanctionId}/link-proposals`, { proposals });
    return response.data.data;
  },

  async unlinkProposal(sanctionId: string | number, proposalId: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/admin-sanctions/${sanctionId}/proposals/${proposalId}`);
    return response.data.data;
  },

  async createWorks(sanctionId: string | number, worksData?: Array<Record<string, any>>) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions/${sanctionId}/create-works`, { works: worksData || [] });
    return response.data.data;
  },

  async getUnsanctionedWorks(grouped = true) {
    const response = await axiosInstance.get(`${BASE_URL}/admin-sanctions/unsanctioned-works`, { params: { grouped } });
    return response.data.data;
  },

  async createFromWorks(data: { workIds: number[]; go_nu?: string; go_dt?: string; fy_id?: number; admn_snctn_nm?: string; sts_cd?: string; snctn_dt?: string; iec_pct?: number; cb_pct?: number }) {
    const response = await axiosInstance.post(`${BASE_URL}/admin-sanctions/create-from-works`, data);
    return response.data;
  }
};

/**
 * Work Schedule API
 */
export const workScheduleApi = {
  async getAll(filters?: WMSFilters & { scheduleStatus?: string; workStatusId?: number; contractorId?: number; workTypeId?: number; udaId?: number; circleId?: number; schemeId?: number; pia?: string; packageTender?: string; packageStatus?: 'packaged' | 'non_packaged'; abstractStatus?: 'with' | 'without'; schedulePresence?: string; search?: string; includeAllStatuses?: boolean | string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit, schedulePresence: filters?.schedulePresence, workStatusId: filters?.workStatusId, contractorId: filters?.contractorId, scheduleStatus: filters?.scheduleStatus, workTypeId: filters?.workTypeId, udaId: filters?.udaId, circleId: filters?.circleId, schemeId: filters?.schemeId, pia: filters?.pia, packageTender: filters?.packageTender, packageStatus: filters?.packageStatus, abstractStatus: filters?.abstractStatus, search: filters?.search, includeAllStatuses: filters?.includeAllStatuses, onlyPushkaralu: (filters as any)?.onlyPushkaralu };
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  // KPI counts over the list's FILTERED population — same params as getAll, minus the two
  // card drills (scheduleStatus, schedulePresence), so the cards follow the filter panel
  // and each card counts exactly the rows it would list.
  async getFilteredCounts(filters?: Record<string, any>) {
    const f = filters || {};
    const params = { ...buildFilterParams(f as any), workStatusId: f.workStatusId, contractorId: f.contractorId, workTypeId: f.workTypeId, udaId: f.udaId, circleId: f.circleId, schemeId: f.schemeId, pia: f.pia, packageTender: f.packageTender, packageStatus: f.packageStatus, abstractStatus: f.abstractStatus, search: f.search, includeAllStatuses: f.includeAllStatuses, onlyPushkaralu: f.onlyPushkaralu };
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/filtered-counts`, { params });
    return response.data as { total: number; scheduleStats: Record<string, number>; statuses: { code: string; label: string; count: number }[] };
  },

  // udaId is passed through so the KPI cards count the same population the list
  // shows. Without it the cards read every UDA while the grid read one.
  async getStatusCounts(udaId?: number | string) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/status-counts`, { params: { udaId } });
    return response.data;
  },

  // Construction-Program export data (powers the Download Schedule popup).
  async getProgramData(schemeIds: number[], udaIds: number[], contractorIds: number[] = []) {
    const params: Record<string, string> = {};
    if (schemeIds.length)     params.schemeIds     = schemeIds.join(',');
    if (udaIds.length)        params.udaIds        = udaIds.join(',');
    if (contractorIds.length) params.contractorIds = contractorIds.join(',');
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/program-data`, { params });
    return response.data.data;
  },

  async getScheduleStatusCounts(udaId?: number | string) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/schedule-status-counts`, { params: { udaId } });
    return response.data.data;
  },

  async getById(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/${workId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/work-schedules`, data);
    return response.data.data;
  },

  async update(scheduleId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/work-schedules/${scheduleId}`, data);
    return response.data.data;
  },

  async delete(scheduleId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/work-schedules/${scheduleId}`);
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/schedule`);
    return response.data.data;
  },

  async updateScheduleStatus(scheduleId: string | number, status: string, rmrks?: string) {
    const response = await axiosInstance.put(`${BASE_URL}/work-schedules/${scheduleId}/status`, { status, rmrks });
    return response.data.data;
  },

  async getTemplates(filters?: { schme_id?: number; wrk_ntr_id?: number; wrk_type_id?: number; wrk_ctgry_id?: number }) {
    const params = new URLSearchParams();
    if (filters?.schme_id) params.append('schme_id', String(filters.schme_id));
    if (filters?.wrk_ntr_id) params.append('wrk_ntr_id', String(filters.wrk_ntr_id));
    if (filters?.wrk_type_id) params.append('wrk_type_id', String(filters.wrk_type_id));
    if (filters?.wrk_ctgry_id) params.append('wrk_ctgry_id', String(filters.wrk_ctgry_id));
    const qs = params.toString();
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/templates${qs ? '?' + qs : ''}`);
    return response.data.data;
  },

  async getTemplateMilestones(templateId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/templates/${templateId}/milestones`);
    return response.data.data;
  },

  async assignFromTemplate(scheduleId: string | number, templateId: string | number) {
    const response = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/assign-template`, { templateId });
    return response.data.data;
  },

  async copyFromProject(scheduleId: string | number, sourceWorkId: string | number) {
    const response = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/copy-from-project`, { sourceWorkId });
    return response.data.data;
  },

  async getSimilarWorks(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/similar-works`);
    return response.data.data;
  },

  async addMilestone(scheduleId: string | number, milestoneData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/milestones`, milestoneData);
    return response.data.data;
  },

  async updateMilestone(milestoneId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/work-schedules/milestones/${milestoneId}`, data);
    return response.data.data;
  },

  async deleteMilestone(milestoneId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/work-schedules/milestones/${milestoneId}`);
  },

  async updateProgress(scheduleId: string | number, progressData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/progress`, progressData);
    return response.data.data;
  },

  async getProgressHistory(scheduleId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/${scheduleId}/progress-history`);
    return response.data.data;
  },

  // Carry-forward shortfalls from past (locked) milestones, attached to the
  // next active milestone. Returns { target_wrk_schd_mlstn_id, items: [...] }.
  async getCarryForward(scheduleId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/${scheduleId}/carry-forward`);
    return response.data.data;
  },

  // Financial Progress (decoupled from physical). Body keys:
  //   wrk_schd_mlstn_id, plnd_amt_at, snctn_amt_at, expndtr_at, rmrk_tx
  // Balance is computed by Postgres; fncl_prgrs_nu is recomputed server-side.
  async updateFinancialProgress(scheduleId: string | number, payload: Record<string, any>) {
    const response = await axiosInstance.post(
      `${BASE_URL}/work-schedules/${scheduleId}/financial-progress`, payload
    );
    return response.data.data;
  },
  async getFinancialHistory(scheduleId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/${scheduleId}/financial-history`);
    return response.data.data;
  },
  async getLatestFinancialPerMilestone(scheduleId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/${scheduleId}/financial-latest`);
    return response.data.data;
  },

  // Physical Progress (period-based)
  async getPhysicalProgress(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/physical-progress`);
    return response.data.data;
  },

  async addPhysicalProgress(workId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/physical-progress`, data);
    return response.data.data;
  },

  async updatePhysicalProgress(id: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/physical-progress/${id}`, data);
    return response.data.data;
  },

  async deletePhysicalProgress(id: string | number) {
    await axiosInstance.delete(`${BASE_URL}/physical-progress/${id}`);
  },

  async getGanttData(filters?: WMSFilters) {
    const params = buildFilterParams(filters);
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/gantt`, { params });
    return response.data.data;
  },

  async assignContractor(workId: string | number, contractorId: string | number) {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/assign-contractor`, { contractorId });
    return response.data.data;
  },

  async getFinanceSchedule(scheduleId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-schedules/${scheduleId}/finance-schedule`);
    return response.data.data;
  },

  async saveFinanceSchedule(scheduleId: string | number, entries: any[]) {
    const response = await axiosInstance.put(`${BASE_URL}/work-schedules/${scheduleId}/finance-schedule`, { entries });
    return response.data.data;
  },

  async getFinancialSummary(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/financial-summary`);
    return response.data.data;
  }
};

/**
 * Components Catalog — backs the COMPONENT / Sub-Component dropdowns
 * in the Add Milestone modal. Scoped per scheme (schme_id).
 */
export interface CmpntSubItem {
  sub_cmpnt_id: number;
  sub_cmpnt_cd: string;
  sub_cmpnt_nm: string;
  seq_nu?: number;
}
export interface CmpntItem {
  cmpnt_id: number;
  cmpnt_cd: string;
  cmpnt_nm: string;
  seq_nu?: number;
  sub_components: CmpntSubItem[];
}

export const componentsApi = {
  async list(): Promise<CmpntItem[]> {
    const response = await axiosInstance.get(`${BASE_URL}/components`);
    return response.data.data;
  },
  async create(cmpnt_nm: string): Promise<CmpntItem> {
    const response = await axiosInstance.post(`${BASE_URL}/components`, { cmpnt_nm });
    return response.data.data;
  },
  async createSub(parentId: number, sub_cmpnt_nm: string): Promise<CmpntSubItem> {
    const response = await axiosInstance.post(`${BASE_URL}/components/${parentId}/sub-components`, { sub_cmpnt_nm });
    return response.data.data;
  },
};

/**
 * Custom Milestone Fields — per-sub-component fields defined when adding a
 * milestone, rendered/answered on the Progress Update screen.
 */
export interface CstmMleFieldDef {
  cstm_mle_id?: number;
  schme_id?: number;
  cmpnt_id?: number;
  sub_cmpnt_id?: number;
  wrk_id?: number;
  fld_cd?: string;
  fld_nm: string;
  fld_type_cd: string; // 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'radio' | 'checkbox'
  is_rqrd_in?: boolean;
  plchldr_tx?: string | null;
  optns_jn?: Array<{ label: string; value: string }> | null;
  seq_nu?: number;
  // Joined from catalog tables on the read endpoint so the frontend can
  // match by display name when the live sub-instance lacks an FK column.
  cmpnt_nm?: string;
  sub_cmpnt_nm?: string;
}
export interface CstmMleValue {
  val_id?: number;
  cstm_mle_id: number;
  wrk_schd_sub_mlstn_id: number;
  fld_val_tx: string | null;
}

export interface FldType { fld_type_cd: string; fld_type_nm: string; seq_nu?: number; }

export const cstmMleApi = {
  async listFieldTypes(): Promise<FldType[]> {
    const r = await axiosInstance.get(`${BASE_URL}/cstm-mle/field-types`);
    return r.data.data;
  },
  async listForWork(wrkId: number, subCmpntId?: number): Promise<CstmMleFieldDef[]> {
    const params: any = { wrk_id: wrkId };
    if (subCmpntId) params.sub_cmpnt_id = subCmpntId;
    const r = await axiosInstance.get(`${BASE_URL}/cstm-mle`, { params });
    return r.data.data;
  },
  async upsertForSub(payload: {
    schme_id: number; wrk_id: number; cmpnt_id: number; sub_cmpnt_id: number;
    fields: CstmMleFieldDef[];
  }): Promise<CstmMleFieldDef[]> {
    const r = await axiosInstance.post(`${BASE_URL}/cstm-mle`, payload);
    return r.data.data;
  },
  async saveValues(payload: { wrk_prgrs_id: number; values: CstmMleValue[] }) {
    const r = await axiosInstance.post(`${BASE_URL}/cstm-mle/values`, payload);
    return r.data.data;
  },
  async latestValues(wrkId: number) {
    const r = await axiosInstance.get(`${BASE_URL}/cstm-mle/values/latest`, { params: { wrk_id: wrkId } });
    return r.data.data;
  },
};

/**
 * Attachment API — BYTEA-backed binary store for file/image milestone fields.
 * The progress payload only carries `attchmnt_id` + metadata; the binary is
 * downloaded on demand via the URL helper below.
 */
export const attachmentApi = {
  async upload(file: File, refKind?: string, refId?: number | string) {
    const fd = new FormData();
    // MUST be 'files': the server's global multer is upload.array('files') and
    // rejects any other field name with 400 LIMIT_UNEXPECTED_FILE before the
    // request reaches the route.
    fd.append('files', file);
    if (refKind) fd.append('ref_kind', refKind);
    if (refId !== undefined && refId !== null) fd.append('ref_id', String(refId));
    const response = await axiosInstance.post(`${BASE_URL}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data as {
      attchmnt_id: number;
      file_nm: string;
      cntnt_type: string;
      file_sz: number;
    };
  },
  /** Attachment count per owner for one ref_kind — one call for a whole grid. */
  async counts(refKind: string): Promise<Record<number, number>> {
    const response = await axiosInstance.get(`${BASE_URL}/attachments/counts`, { params: { ref_kind: refKind } });
    const out: Record<number, number> = {};
    (response.data.data || []).forEach((r: { ref_id: number | string; n: number }) => {
      out[Number(r.ref_id)] = Number(r.n);
    });
    return out;
  },
  url(attchmntId: number | string) {
    // Build an ABSOLUTE URL that the browser can navigate to directly (e.g. for
    // <a href> View / Download links opened in a new tab). Axios prepends the
    // baseURL automatically for its own requests, but a plain <a href> resolves
    // relative paths against the current page (http://localhost:3000/...) which
    // hits the frontend SPA instead of the backend. Combine the axios baseURL
    // (http://localhost:4901/apiv1/) with the route to get the real endpoint.
    const apiRoot = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
    return `${apiRoot}/${BASE_URL}/attachments/${attchmntId}`;
  },
  /**
   * Fetch the attachment's bytes via axios (so the JWT bearer token added by the
   * shared interceptor travels with the request) and wrap them in a same-origin
   * blob URL the browser can iframe / open directly.
   *
   * Use this for any in-app preview surface (PdfPreviewModal, etc.) where a
   * plain `<iframe src=url(id)>` would hit the backend without auth and render
   * the browser's broken-document icon. Callers OWN the returned URL and must
   * call URL.revokeObjectURL on it once the consumer is unmounted.
   */
  async fetchBlobUrl(attchmntId: number | string): Promise<string> {
    const response = await axiosInstance.get(`${BASE_URL}/attachments/${attchmntId}`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data as Blob);
  },
  /** List files saved against a logical owner (e.g. the signed schedule PDF). */
  async listByRef(refKind: string, refId: number | string) {
    const response = await axiosInstance.get(`${BASE_URL}/attachments`, {
      params: { ref_kind: refKind, ref_id: refId },
    });
    return response.data.data as Array<{
      attchmnt_id: number;
      file_nm: string;
      cntnt_type: string;
      file_sz: number;
      i_ts: string;
    }>;
  },
  async list(refKind: string, refId: number | string) {
    return this.listByRef(refKind, refId);
  },
  async remove(attchmntId: number | string) {
    await axiosInstance.delete(`${BASE_URL}/attachments/${attchmntId}`);
  },
  /**
   * Unified "Site Photos" feed for a work — aggregates every progress photo
   * across all upload paths (milestone Progress Updates, BOQ/abstract progress,
   * work component images) into one newest-first list. Powers the Work Profile
   * gallery + PDF so photos show regardless of which screen captured them.
   */
  async listWorkPhotos(workId: number | string) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/photos`);
    return response.data.data as Array<{
      attchmnt_id: number;
      file_nm: string;
      cntnt_type: string;
      file_sz: number;
      i_ts: string;
      source: 'milestone' | 'boq' | 'component';
      source_label: string | null;
      ref_kind: string;
      ref_id: number;
      captured_dt: string | null;
    }>;
  },
};

/**
 * Work-Schedule approval chain (Contractor -> EE -> SE -> ...).
 * State is derived server-side from ds_wms.wms_wrk_schd_apprvl_t. Signed PDFs
 * for sign-forward / finalize go on the multer 'files' field.
 */
export const scheduleApprovalApi = {
  async getState(scheduleId: number | string) {
    const res = await axiosInstance.get(`${BASE_URL}/work-schedules/${scheduleId}/approval`);
    return res.data.data as {
      stages: Array<{ code: string; label: string; canEdit: boolean; initiator: boolean; final: boolean }>;
      state: { statusCd: string; stageCode: string | null; isFinalized: boolean; canEditStageCode: string | null };
      // isAdmin: backend-resolved admin/super-user. canCorrect: may "unlock to
      // correct" a finalized schedule's Schedule Targets (admins + configured
      // correction designations, e.g. DSE). Optional for backward-compatibility.
      actor: { stageCode: string | null; rleId: number | null; isAdmin?: boolean; canCorrect?: boolean };
      available: string[];
      // Set only when this login can forward. vacant = nobody holds the next post in the
      // work's UDA, so the sender must pick one of `candidates` (that UDA's employees).
      nextStage?: { code: string; label: string; vacant: boolean; candidates: Array<{ usrId: number; name: string; dsgnNm: string | null }> } | null;
      history: Array<{ apprvl_id: number; stage_cd: string; action_cd: string; to_stage_cd: string | null; actor_rle_nm: string | null; actor_nm?: string | null; actor_dsgn_nm?: string | null; stand_in?: boolean; attchmnt_id: number | null; file_nm: string | null; rmrks_tx: string | null; i_ts: string }>;
    };
  },
  async submit(scheduleId: number | string, rmrks?: string, toStage?: string, file?: File | null, assigneeUsrId?: number) {
    const fd = new FormData();
    if (file) fd.append('files', file);
    if (rmrks) fd.append('rmrks', rmrks);
    if (toStage) fd.append('toStage', toStage);
    if (assigneeUsrId != null) fd.append('assigneeUsrId', String(assigneeUsrId));
    const res = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/approval/submit`, fd);
    return res.data.data;
  },
  async signForward(scheduleId: number | string, file: File | null, rmrks?: string, toStage?: string, assigneeUsrId?: number) {
    const fd = new FormData();
    if (file) fd.append('files', file);
    if (rmrks) fd.append('rmrks', rmrks);
    if (toStage) fd.append('toStage', toStage);
    if (assigneeUsrId != null) fd.append('assigneeUsrId', String(assigneeUsrId));
    const res = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/approval/sign-forward`, fd);
    return res.data.data;
  },
  async finalize(scheduleId: number | string, file: File, rmrks?: string) {
    const fd = new FormData();
    fd.append('files', file);
    if (rmrks) fd.append('rmrks', rmrks);
    const res = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/approval/finalize`, fd);
    return res.data.data;
  },
  async returnBack(scheduleId: number | string, rmrks: string) {
    const res = await axiosInstance.post(`${BASE_URL}/work-schedules/${scheduleId}/approval/return`, { rmrks });
    return res.data.data;
  },
  // Notification-bell inbox: schedules currently waiting at the logged-in
  // reviewer's stage (EE sees contractor → EE hand-offs; SE sees EE → SE).
  async pending() {
    const res = await axiosInstance.get(`${BASE_URL}/work-schedules/approval/pending`);
    return res.data.data as {
      actor: { stageCode: string | null; stageLabel: string | null; rleId: number | null; isAdmin: boolean };
      items: Array<{
        wrk_schd_id: number; wrk_id: number; wrk_cd: string; wrk_nm: string;
        ulb_nm: string | null; schme_nm: string | null;
        from_stage_cd: string; from_stage_label: string;
        to_stage_cd: string; to_stage_label: string;
        action_cd: string; actor_rle_nm: string | null; rmrks_tx: string | null; i_ts: string;
      }>;
    };
  },
};

/**
 * Delay & EOT API — Hindrance management and Extension of Time requests
 */
export const delayEotApi = {
  async getHindranceTypes() {
    const response = await axiosInstance.get(`${BASE_URL}/hindrances/types`);
    return response.data.data;
  },

  async getHindrances(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/hindrances`);
    return response.data.data;
  },

  async createHindrance(workId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/hindrances`, data);
    return response.data.data;
  },

  // Officers a hindrance can be escalated to in its work's authority. Same shape as
  // getEOTAssignees — { configured, designation, candidates, default }.
  async getHindranceAssignees(hindranceId: string | number, stage = 'ESCALATED') {
    const response = await axiosInstance.get(`${BASE_URL}/hindrances/${hindranceId}/workflow-assignees`, { params: { stage } });
    return response.data.data;
  },

  async updateHindrance(hindranceId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/hindrances/${hindranceId}`, data);
    return response.data.data;
  },

  async getEOTRequests(contractId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/contracts/${contractId}/eot`);
    return response.data.data;
  },

  async createEOTRequest(contractId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/contracts/${contractId}/eot`, data);
    return response.data.data;
  },

  async approveEOT(eotId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/eot/${eotId}/approve`, data);
    return response.data.data;
  },

  async rejectEOT(eotId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/eot/${eotId}/reject`, data);
    return response.data.data;
  },

  // The submitting act. An EOT is created as a DRAFT and reaches an approver only
  // through here, optionally addressed to a chosen officer (asgn_pos_id /
  // asgn_usr_id); absent one, the server routes by the authority's configuration.
  async submitEOT(eotId: string | number, data: Record<string, any> = {}) {
    const response = await axiosInstance.put(`${BASE_URL}/eot/${eotId}/submit`, data);
    return response.data.data;
  },

  // Back to the requester for correction. The server requires rmrks_tx.
  async returnEOT(eotId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/eot/${eotId}/return`, data);
    return response.data.data;
  },

  // Officers this EOT can be submitted to in its own authority. Answers
  // { configured, designation, candidates, default } — configured:false means the
  // authority has no routing rule, which the screen must say rather than showing an
  // empty list that reads as "nobody is available".
  async getEOTAssignees(eotId: string | number, stage = 'SUBMITTED') {
    const response = await axiosInstance.get(`${BASE_URL}/eot/${eotId}/workflow-assignees`, { params: { stage } });
    return response.data.data;
  },

  async getEOTHistory(eotId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/eot/${eotId}/history`);
    return response.data.data;
  }
};

/**
 * Designs API - Track work designs from proposal to grounding
 */
export const designsApi = {
  /**
   * Design stage transitions (migration 166). Act on a WORK, because the designs list
   * is grouped one row per work. Each records who acted and when.
   *
   * There is no `ground` here on purpose: a design is grounded because its WORK reaches
   * GROUNDED, which the server does inside the work's own transaction.
   */
  // Officers a drawing can be sent to at a stage. Same shape as the proposal and estimate
  // versions so SubmitToOfficerDialog renders all three without branching.
  async getWorkflowAssignees(drwngId: string | number, stage: string) {
    const response = await axiosInstance.get(
      `${BASE_URL}/designs/${drwngId}/workflow-assignees`, { params: { stage } });
    return response.data.data as {
      prpsl_cd: string; prpsl_nm: string; stage: string; uda_id: number | null;
      configured: boolean; nxt_actn_tx: string | null;
      designation: { id: number; name: string; rank: number } | null;
      default: WorkflowAssignee | null;
      candidates: WorkflowAssignee[];
      /* The whole assignable directory for this record's authority, as
       * Department -> Designation -> Employee. `candidates` above is the routing
       * matrix's answer and stays the default; this is the browse path, and it
       * carries the posts the rank ladder cannot score. */
      tree: WorkflowAssigneeDept[];
    };
  },

  async setStage(wrkId: number | string, action: 'start-design' | 'submit' | 'start-review' | 'approve' | 'reject', body: Record<string, any> = {}) {
    const response = await axiosInstance.post(`${BASE_URL}/designs/work/${wrkId}/${action}`, body);
    return response.data;
  },

  async getAll(filters?: WMSFilters & { designStatus?: string; designStage?: string; workId?: number | string; search?: string }, page = 1, limit = 50) {
    // buildFilterParams drops workId/designStage/search — forward them explicitly
    // so "designs for this work" actually filters by work (prevents false duplicate warnings).
    const extra: Record<string, any> = {};
    if (filters?.workId !== undefined && filters.workId !== null && String(filters.workId) !== '') extra.workId = filters.workId;
    if (filters?.designStage) extra.designStage = filters.designStage;
    if ((filters as any)?.search) extra.search = (filters as any).search;
    const params = { ...buildFilterParams(filters), ...extra, page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/designs`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  /**
   * Card totals for the Designs screen, counted server-side over every work in scope.
   * The page used to derive these from the arrays it already held — the current page
   * of 50 designs and a 500-row slice of works/estimates — so the numbers moved when
   * the user changed page and understated everything past the first slice.
   */
  async getSummary(filters?: WMSFilters) {
    const response = await axiosInstance.get(`${BASE_URL}/designs/summary`, { params: buildFilterParams(filters) });
    return response.data.data as {
      works: { total: number; withDesign: number; withEstimate: number; drawings: number };
      approvedForEstimate: number;
      design: { pending: number; uploaded: number; approved: number; rejected: number };
      estimate: { pending: number; uploaded: number; approved: number };
    };
  },

  async getById(designId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/designs/${designId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/designs`, data);
    return response.data.data;
  },

  async update(designId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/designs/${designId}`, data);
    return response.data.data;
  },

  async delete(designId: string | number) {
    await axiosInstance.delete(`${BASE_URL}/designs/${designId}`);
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/designs`);
    return response.data.data;
  },

  async submitForReview(designId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/designs/${designId}/submit`, data);
    return response.data.data;
  },

  async approve(designId: string | number, approvalData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/designs/${designId}/approve`, approvalData);
    return response.data.data;
  },

  async reject(designId: string | number, rejectionData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/designs/${designId}/reject`, rejectionData);
    return response.data.data;
  },

  // markGrounded() was removed: it posted to POST /designs/:id/ground, a route that has
  // never existed on the server, so every call would have 404'd. Nothing called it.
  //
  // It is not being replaced. A design is grounded because its WORK reaches GROUNDED —
  // worksApi.ground() below — which the server applies to the work's approved designs
  // inside the same transaction. Grounding a drawing on its own would let a design
  // claim the work had started when it had not.

  async getRevisions(designId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/designs/${designId}/revisions`);
    return response.data.data;
  },

  async getDrawings(designId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/designs/${designId}/drawings`);
    return response.data.data;
  },

  async uploadDrawing(designId: string | number, drawingData: FormData) {
    // Don't manually set Content-Type - axios will auto-set it with correct boundary when it detects FormData
    const response = await axiosInstance.post(`${BASE_URL}/designs/${designId}/drawings`, drawingData);
    return response.data.data;
  },

  async deleteDrawing(designId: string | number, drawingId: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/designs/${designId}/drawings/${drawingId}`);
    return response.data;
  },

  async updateDrawing(designId: string | number, drawingId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/designs/${designId}/drawings/${drawingId}`, data);
    return response.data.data;
  },

  async getDigitalTwinFiles(designId?: string | number) {
    const params = designId ? { designId } : {};
    const response = await axiosInstance.get(`${BASE_URL}/designs/digital-twin-files`, { params });
    return response.data.data;
  },

  async convertDrawing(designId: string | number, drawingId: string | number) {
    const response = await axiosInstance.post(`${BASE_URL}/designs/${designId}/drawings/${drawingId}/convert`);
    return response.data;
  },

  async getConversionStatus(designId: string | number, drawingId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/designs/${designId}/drawings/${drawingId}/conversion-status`);
    return response.data.data;
  },

  async getDesignStages() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/design-stages`);
    return response.data.data;
  }
};

/**
 * Sanctions API
 */
export const sanctionsApi = {
  async getAll(filters?: WMSFilters & { sanctionType?: string; status?: string }, page = 1, limit = 50) {
    const params = { ...buildFilterParams(filters), page, limit };
    const response = await axiosInstance.get(`${BASE_URL}/sanctions`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  async getById(sanctionId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/sanctions/${sanctionId}`);
    return response.data.data;
  },

  async create(data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/sanctions`, data);
    return response.data.data;
  },

  async update(sanctionId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/sanctions/${sanctionId}`, data);
    return response.data.data;
  },

  async approve(sanctionId: string | number, approvalData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/sanctions/${sanctionId}/approve`, approvalData);
    return response.data.data;
  },

  async reject(sanctionId: string | number, rejectionData: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/sanctions/${sanctionId}/reject`, rejectionData);
    return response.data.data;
  },

  async getByWork(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/sanctions`);
    return response.data.data;
  },

  async getSanctionTypes() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sanction-types`);
    return response.data.data;
  }
};

// Add more methods to mastersApi
Object.assign(mastersApi, {
  async getContractorCategories(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/contractor-categories`, { params: { activeOnly } });
    return response.data.data;
  },

  async getContractorStatuses(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/contractor-statuses`, { params: { activeOnly } });
    return response.data.data;
  },

  async getTenderTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/tender-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getEvalMethods(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/eval-methods`, { params: { activeOnly } });
    return response.data.data;
  },

  async getTenderStatuses(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/tender-statuses`, { params: { activeOnly } });
    return response.data.data;
  },

  async getUOMs(activeOnly = true, uomType?: string) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/uom`, { params: { activeOnly, uomType } });
    return response.data.data;
  },

  async getBillTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/bill-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getInspectionTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/inspection-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getQCTestTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/qc-test-types`, { params: { activeOnly } });
    return response.data.data;
  },

  // Additional Masters APIs
  async getRateContractTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/rate-contract-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getSORs(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sor`, { params: { activeOnly } });
    return response.data.data;
  },

  async getSORItems(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/sor-items`, { params: { activeOnly } });
    return response.data.data;
  },

  async getStatutoryDeductions(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/statutory-deductions`, { params: { activeOnly } });
    return response.data.data;
  },

  async getNoticeTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/notice-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getAuditTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/audit-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getTenderDocStructures(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/tender-doc-structures`, { params: { activeOnly } });
    return response.data.data;
  },

  async getMonitoringPanels(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/monitoring-panels`, { params: { activeOnly } });
    return response.data.data;
  },

  async getMonitoringPanelWorks(panelId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/monitoring-panels/${panelId}/works`);
    return response.data.data;
  },

  async attachWorkToPanel(panelId: string | number, workId: string | number, sequenceNum: number) {
    const response = await axiosInstance.post(`${BASE_URL}/masters/monitoring-panels/${panelId}/works`, { wrk_id: workId, sqnce_nu: sequenceNum });
    return response.data.data;
  },

  async detachWorkFromPanel(panelId: string | number, workId: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/masters/monitoring-panels/${panelId}/works/${workId}`);
    return response.data.data;
  },

  async updatePanelWorkOrder(panelId: string | number, workOrders: Array<{wrk_id: string | number, sqnce_nu: number}>) {
    const response = await axiosInstance.put(`${BASE_URL}/masters/monitoring-panels/${panelId}/works/order`, { workOrders });
    return response.data.data;
  },

  // Notice Templates API
  async getNoticeTemplates(noticeTypeId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/notice-templates/by-notice-type/${noticeTypeId}`);
    return response.data.data;
  },

  async getCurrentNoticeTemplate(noticeTypeId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/notice-templates/current/${noticeTypeId}`);
    return response.data.data;
  },

  async getNoticeTemplateById(templateId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/notice-templates/${templateId}`);
    return response.data.data;
  },

  async saveNoticeTemplate(data: Record<string, any>) {
    if (data.ntce_tmplt_id) {
      const response = await axiosInstance.put(`${BASE_URL}/masters/notice-templates/${data.ntce_tmplt_id}`, data);
      return response.data.data;
    } else {
      const response = await axiosInstance.post(`${BASE_URL}/masters/notice-templates`, data);
      return response.data.data;
    }
  },

  async setCurrentTemplate(templateId: string | number, noticeTypeId: string | number) {
    const response = await axiosInstance.put(`${BASE_URL}/masters/notice-templates/${templateId}/set-current`, { ntce_type_id: noticeTypeId });
    return response.data.data;
  },

  async deleteNoticeTemplate(templateId: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/masters/notice-templates/${templateId}`);
    return response.data;
  },

  // Firm Type APIs
  async getFirmTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/firm-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getFirmTypeById(id: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/firm-types/${id}`);
    return response.data.data;
  },

  async saveFirmType(data: Record<string, any>) {
    if (data.frm_type_id) {
      const response = await axiosInstance.put(`${BASE_URL}/masters/firm-types/${data.frm_type_id}`, data);
      return response.data.data;
    } else {
      const response = await axiosInstance.post(`${BASE_URL}/masters/firm-types`, data);
      return response.data.data;
    }
  },

  async deleteFirmType(id: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/masters/firm-types/${id}`);
    return response.data;
  },

  // Firm Specialization APIs
  async getFirmSpecializations(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/firm-specializations`, { params: { activeOnly } });
    return response.data.data;
  },

  async getFirmSpecializationById(id: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/firm-specializations/${id}`);
    return response.data.data;
  },

  async saveFirmSpecialization(data: Record<string, any>) {
    if (data.frm_spclzn_id) {
      const response = await axiosInstance.put(`${BASE_URL}/masters/firm-specializations/${data.frm_spclzn_id}`, data);
      return response.data.data;
    } else {
      const response = await axiosInstance.post(`${BASE_URL}/masters/firm-specializations`, data);
      return response.data.data;
    }
  },

  async deleteFirmSpecialization(id: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/masters/firm-specializations/${id}`);
    return response.data;
  },

  // Contractor Specializations (multi-select relationship)
  async getContractorSpecializations(contractorId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/contractors/${contractorId}/specializations`);
    return response.data.data;
  },

  async saveContractorSpecializations(contractorId: string | number, specializationIds: number[]) {
    const response = await axiosInstance.post(`${BASE_URL}/masters/contractors/${contractorId}/specializations`, { specialization_ids: specializationIds });
    return response.data.data;
  },

  // Proposal Source APIs
  async getProposalSources(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/proposal-sources`, { params: { activeOnly } });
    return response.data.data;
  },

  async getProposalSourcesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/proposal-sources/list`);
    return response.data.data;
  },

  async getProposalSourceById(id: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/proposal-sources/${id}`);
    return response.data.data;
  },

  async saveProposalSource(data: Record<string, any>) {
    if (data.prpsl_srce_id) {
      const response = await axiosInstance.put(`${BASE_URL}/masters/proposal-sources/${data.prpsl_srce_id}`, data);
      return response.data.data;
    } else {
      const response = await axiosInstance.post(`${BASE_URL}/masters/proposal-sources`, data);
      return response.data.data;
    }
  },

  async deleteProposalSource(id: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/masters/proposal-sources/${id}`);
    return response.data;
  },

  // Proposal Type APIs
  async getProposalTypes(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/proposal-types`, { params: { activeOnly } });
    return response.data.data;
  },

  async getProposalTypesList() {
    const response = await axiosInstance.get(`${BASE_URL}/masters/proposal-types/list`);
    return response.data.data;
  },

  async getProposalTypeById(id: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/proposal-types/${id}`);
    return response.data.data;
  },

  async saveProposalType(data: Record<string, any>) {
    if (data.prpsl_type_id) {
      const response = await axiosInstance.put(`${BASE_URL}/masters/proposal-types/${data.prpsl_type_id}`, data);
      return response.data.data;
    } else {
      const response = await axiosInstance.post(`${BASE_URL}/masters/proposal-types`, data);
      return response.data.data;
    }
  },

  async deleteProposalType(id: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/masters/proposal-types/${id}`);
    return response.data;
  },

  // Priority Level APIs
  async getPriorityLevels(activeOnly = true) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/priority-levels`, { params: { activeOnly } });
    return response.data.data;
  },

  async getPriorityLevelById(id: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/masters/priority-levels/${id}`);
    return response.data.data;
  },

  async savePriorityLevel(data: Record<string, any>) {
    if (data.prty_lvl_id) {
      const response = await axiosInstance.put(`${BASE_URL}/masters/priority-levels/${data.prty_lvl_id}`, data);
      return response.data.data;
    } else {
      const response = await axiosInstance.post(`${BASE_URL}/masters/priority-levels`, data);
      return response.data.data;
    }
  },

  async deletePriorityLevel(id: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/masters/priority-levels/${id}`);
    return response.data;
  }
});

/**
 * Work GIS API - geometry drawing, progress tracking, deviations
 */
export const workGisApi = {
  async getGeometries(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/gis`);
    return response.data.data;
  },
  async saveGeometry(workId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/gis`, data);
    return response.data.data;
  },
  async updateGeometry(workId: string | number, gisRelId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/works/${workId}/gis/${gisRelId}`, data);
    return response.data.data;
  },
  async deleteGeometry(workId: string | number, gisRelId: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/works/${workId}/gis/${gisRelId}`);
    return response.data;
  },
  async getTypeConfig(typeId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/work-types/${typeId}/gis-config`);
    return response.data.data;
  },
  async getProgress(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/gis-progress`);
    return response.data.data;
  },
  // Field-captured mapping done in the mobile app for this work.
  // geo-tags = per-component point/path drawings (wms_wrk_gis_rel_t, geom_ctgry_cd='GEOTAG');
  // mobile-photos = geo-stamped site photos (wms_mobile_photo_t). Both are served from the
  // shared mobile route group, so a read here is exactly what the app wrote in the field.
  async getGeoTags(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/mobile/works/${workId}/geo-tags`);
    return response.data.data;
  },
  async getMobilePhotos(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/mobile/works/${workId}/photos`);
    return response.data.data;
  },
  // Every component / sub-sector image captured in the app for this work. Called
  // without a sub-sector filter, so it returns ALL of them; each row carries its
  // own geo-tag (geo_type / lat / lng / path_jn / addr_tx) when the app recorded one.
  async getComponentImages(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/mobile/works/${workId}/component-images`);
    return response.data.data;
  },
  async saveProgress(workId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/gis-progress`, data);
    return response.data.data;
  },
  async updateProgress(workId: string | number, id: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/works/${workId}/gis-progress/${id}`, data);
    return response.data.data;
  },
  async deleteProgress(workId: string | number, id: string | number) {
    const response = await axiosInstance.delete(`${BASE_URL}/works/${workId}/gis-progress/${id}`);
    return response.data;
  },
  async uploadProgressPhoto(workId: string | number, progressId: string | number, formData: FormData) {
    const response = await axiosInstance.post(
      `${BASE_URL}/works/${workId}/gis-progress/${progressId}/photos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },
  // Deviation detail endpoints
  async getDeviations(workId: string | number) {
    const response = await axiosInstance.get(`${BASE_URL}/works/${workId}/deviations`);
    return response.data.data;
  },
  async saveDeviation(workId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.post(`${BASE_URL}/works/${workId}/deviations`, data);
    return response.data.data;
  },
  async updateDeviation(workId: string | number, dvtnId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/works/${workId}/deviations/${dvtnId}`, data);
    return response.data.data;
  },
  async closeDeviation(workId: string | number, dvtnId: string | number, data: Record<string, any>) {
    const response = await axiosInstance.put(`${BASE_URL}/works/${workId}/deviations/${dvtnId}/close`, data);
    return response.data.data;
  },
  async uploadDeviationPhoto(workId: string | number, dvtnId: string | number, formData: FormData) {
    const response = await axiosInstance.post(
      `${BASE_URL}/works/${workId}/deviations/${dvtnId}/photos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }
};

/**
 * Department Login Groups API — manages the works bifurcation
 * (groups, group↔departments, group↔logins).
 */
export interface DeptGroup {
  grp_id: number;
  grp_cd: string;
  grp_nm: string;
  dscn_tx?: string | null;
  a_in?: boolean;
  /**
   * Real columns since migration 109 — no longer inferred by regex from the text above.
   * acnt_grp_id points at the department ACCOUNT, which is another row of the same
   * table (is_prefltr_in = true). Accounts are ENC / APTIDCO / CDMA / PHMED; they are
   * NOT municipal departments (wms_dpmt_lst_t: Engineering, Water Supply, …).
   */
  acnt_grp_id?: number | null;
  acnt_cd?: string | null;
  acnt_nm?: string | null;
  role_cd?: string | null;
  /** true => department account, ANDed over everything. false => a grant; grants OR together. */
  is_prefltr_in?: boolean;
  enfrce_in?: boolean;
  dept_count?: number;
  user_count?: number;
  scope_count?: number;
  dept_names?: string | null;
}
/** A department account: ENC, APTIDCO, CDMA, PHMED. Backed by a prefilter group row. */
export interface DeptAccount { acnt_id: number; acnt_cd: string; acnt_nm: string; is_cnfgrd_in?: boolean; }
/** Municipal department (Engineering, Water Supply, …) — only the legacy rollup uses this. */
export interface DeptGroupDepartment { dpmt_id: number; dpmt_cd: string; dpmt_nm: string; }
export interface DeptGroupEntityType {
  entity_type_id: number; entity_type_cd: string; entity_type_nm: string;
  icon_cd?: string | null; clr_cd?: string | null; hint_tx?: string | null; sqnce_nu?: number;
}
export interface DeptGroupEntityOption { id: number; code: string | null; name: string; }
export interface DeptGroupUser { usr_id: number; tenant_id?: string | null; acnt_nm?: string | null; username?: string | null; name?: string | null; }

export const deptGroupsApi = {
  async list(params?: { acnt_id?: number; role_cd?: string; q?: string; kind?: 'GRANT' | 'PREFILTER' }): Promise<DeptGroup[]> {
    const r = await axiosInstance.get<ApiResponse<DeptGroup[]>>(`${BASE_URL}/dept-groups`, { params });
    return r.data.data;
  },
  async create(body: { grp_cd: string; grp_nm: string; acnt_id: number; role_cd?: string; dscn_tx?: string }): Promise<DeptGroup> {
    const r = await axiosInstance.post<ApiResponse<DeptGroup>>(`${BASE_URL}/dept-groups`, body);
    return r.data.data;
  },
  async update(grpId: number, body: { grp_cd?: string; grp_nm?: string; acnt_id?: number; role_cd?: string; dscn_tx?: string }): Promise<DeptGroup> {
    const r = await axiosInstance.put<ApiResponse<DeptGroup>>(`${BASE_URL}/dept-groups/${grpId}`, body);
    return r.data.data;
  },
  async remove(grpId: number): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/dept-groups/${grpId}`);
  },
  async getDepartments(grpId: number): Promise<DeptGroupDepartment[]> {
    const r = await axiosInstance.get<ApiResponse<DeptGroupDepartment[]>>(`${BASE_URL}/dept-groups/${grpId}/departments`);
    return r.data.data;
  },
  async setDepartments(grpId: number, departmentIds: number[]): Promise<void> {
    await axiosInstance.put(`${BASE_URL}/dept-groups/${grpId}/departments`, { departmentIds });
  },
  /** Pass acnt_id to get only the dimensions that department account may bifurcate on. */
  async listEntityTypes(acntId?: number): Promise<DeptGroupEntityType[]> {
    const r = await axiosInstance.get<ApiResponse<DeptGroupEntityType[]>>(
      `${BASE_URL}/dept-groups-entity-types/list`, { params: acntId ? { acnt_id: acntId } : undefined });
    return r.data.data;
  },
  /** Searched + capped. `include` keeps already-selected ids visible outside the search. */
  async listEntityOptions(cd: string, opts?: { q?: string; limit?: number; include?: number[] }): Promise<DeptGroupEntityOption[]> {
    const r = await axiosInstance.get<ApiResponse<DeptGroupEntityOption[]>>(
      `${BASE_URL}/dept-groups-entity-options/${cd}`,
      { params: { q: opts?.q || undefined, limit: opts?.limit, include: opts?.include?.length ? opts.include.join(',') : undefined } });
    return r.data.data;
  },
  /** All dimensions at once — the screen used to fire one request per dimension. */
  async getScopes(grpId: number): Promise<Record<string, number[]>> {
    const r = await axiosInstance.get<ApiResponse<Record<string, number[]>>>(`${BASE_URL}/dept-groups/${grpId}/scopes`);
    return r.data.data;
  },
  /** Saves every dimension in ONE transaction. A rule is a conjunction; half-saving it grants access it was never meant to. */
  async setScopes(grpId: number, scopes: Record<string, number[]>): Promise<{ scope_count: number; is_empty: boolean }> {
    const r = await axiosInstance.put<ApiResponse<{ scope_count: number; is_empty: boolean }>>(`${BASE_URL}/dept-groups/${grpId}/scopes`, { scopes });
    return r.data.data;
  },
  async getScope(grpId: number, cd: string): Promise<number[]> {
    const r = await axiosInstance.get<ApiResponse<number[]>>(`${BASE_URL}/dept-groups/${grpId}/scopes/${cd}`);
    return r.data.data;
  },
  async setScope(grpId: number, cd: string, entityIds: number[]): Promise<void> {
    await axiosInstance.put(`${BASE_URL}/dept-groups/${grpId}/scopes/${cd}`, { entityIds });
  },
  async getUsers(grpId: number): Promise<DeptGroupUser[]> {
    const r = await axiosInstance.get<ApiResponse<DeptGroupUser[]>>(`${BASE_URL}/dept-groups/${grpId}/users`);
    return r.data.data;
  },
  async addUser(grpId: number, user: { usr_id: number; tenant_id?: string; acnt_nm?: string }): Promise<void> {
    await axiosInstance.post(`${BASE_URL}/dept-groups/${grpId}/users`, user);
  },
  async removeUser(grpId: number, usrId: number): Promise<void> {
    await axiosInstance.delete(`${BASE_URL}/dept-groups/${grpId}/users/${usrId}`);
  },
  async addUsersByAcnt(grpId: number, acntTag: string): Promise<{ matched: number; inserted: number }> {
    const r = await axiosInstance.post<ApiResponse<{ matched: number; inserted: number }>>(`${BASE_URL}/dept-groups/${grpId}/users/by-acnt`, { acntTag });
    return r.data.data;
  },
  async listDepartments(): Promise<DeptGroupDepartment[]> {
    const r = await axiosInstance.get<ApiResponse<DeptGroupDepartment[]>>(`${BASE_URL}/dept-groups-departments/list`);
    return r.data.data;
  }
};

/* ─────────────────────────── Work Assignment API ───────────────────────────
 * OWNERSHIP of works and packages (wms_asgnmt_t). Distinct from deptGroupsApi
 * above, which configures VISIBILITY. Effective access is the union of the two;
 * assignment always widens, never narrows.
 * ------------------------------------------------------------------------- */

export type Coverage = 'ASSIGNED' | 'PARTIAL' | 'UNASSIGNED';
export type PrincipalType = 'USER' | 'ROLE' | 'POSITION' | 'DEPT_ROLE';

export interface AsgnmtRole {
  asgnmt_role_cd: string; asgnmt_role_nm: string; dscn_tx?: string | null;
  is_exclsv_in: boolean; clr_cd?: string | null; icon_cd?: string | null; sqnce_nu?: number;
}
export interface AccountConfig {
  account: DeptAccount;
  roles: AsgnmtRole[];
  dimensions: DeptGroupEntityType[];
}
export interface Principal {
  principal_type: PrincipalType; principal_id: number; role_cd?: string | null;
  username?: string | null; name?: string | null; mobile?: string | null;
}
export interface Holder {
  role_cd: string; principal_id: number; principal_type: PrincipalType;
  /** WORK = assigned directly. PACKAGE = inherited from the package row. */
  src_cd?: 'WORK' | 'PACKAGE'; asgnmt_id: number;
}
export interface AsgnmtPackage {
  // No crcl_id / schme_id: migration 075 never added them to wms_wrk_pkge_lst_t.
  wrk_pkge_id: number; wrk_pkge_cd: string; wrk_pkge_nm: string;
  totl_wrk_nu: number; asgnd_wrk_nu: number; cvrge_cd: Coverage;
  pkge_holders: Holder[] | null;
}
export interface AsgnmtWork {
  wrk_id: number; wrk_cd: string; wrk_nm: string;
  wrk_sts_id?: number | null; wrk_sts_nm?: string | null;
  ulb_id?: number | null; ulb_nm?: string | null;
  wrk_type_id?: number | null; schme_id?: number | null;
  // Rich display columns, present only in the flat works list (listWorks).
  crcl_id?: number | null; crcl_nm?: string | null; schme_nm?: string | null;
  cntrctr_id?: number | null; cntrctr_nm?: string | null;
  phscl_prgrs_nu?: number | null; fncl_prgrs_nu?: number | null;
  total_val_at?: number | null; cntrct_val_at?: number | null;
  // The package this work sits in (flat list only).
  wrk_pkge_id?: number | null; wrk_pkge_cd?: string | null; wrk_pkge_nm?: string | null;
  holders: Holder[];
}
export interface AsgnmtPreview {
  role: AsgnmtRole;
  creates: Array<{ wrk_id?: number; wrk_pkge_id?: number }>;
  reassigns: Array<{ wrk_id?: number; wrk_pkge_id?: number; from_principal_id: number; asgnmt_id: number }>;
  noops: Array<{ wrk_id?: number; wrk_pkge_id?: number }>;
  conflicts: Array<{ kind: string; wrk_id?: number; wrk_pkge_id?: number; message: string }>;
  blocked: boolean;
}
export interface AsgnmtSummary {
  works: { totl_wrk_nu: number; asgnd_wrk_nu: number; unasgnd_wrk_nu: number };
  packages: { totl_pkge_nu: number; unasgnd_pkge_nu: number; prtl_pkge_nu: number; asgnd_pkge_nu: number };
}
export interface AsgnmtHistoryRow {
  hst_id: number; asgnmt_id: number; actn_cd: string;
  prev_principal_id?: number | null; new_principal_id?: number | null;
  prev_sts_cd?: string | null; new_sts_cd?: string | null;
  wrk_id?: number | null; wrk_cd?: string | null; wrk_nm?: string | null;
  wrk_pkge_id?: number | null; wrk_pkge_cd?: string | null;
  asgnmt_role_cd?: string | null; rsn_tx?: string | null;
  actor_usr_id: number; i_ts: string;
}
export interface EffectiveAccess {
  principal_id: number; unrestricted: boolean; denyAll: boolean; reason: string;
  explain: Array<{ kind: string; grpId?: number; dims?: Array<{ cd: string; ids: number[] }>; reason?: string }>;
  visible_work_count: number; works: AsgnmtWork[]; truncated: boolean;
}

export interface AssignBody {
  acnt_id: number;
  asgnmt_role_cd: string;
  principal_id: number;
  principal_type?: PrincipalType;
  wrk_ids?: number[];
  wrk_pkge_ids?: number[];
  rsn_tx?: string;
}

export const assignmentsApi = {
  /** Department accounts: ENC, APTIDCO, CDMA, PHMED. */
  async listAccounts(): Promise<DeptAccount[]> {
    const r = await axiosInstance.get<ApiResponse<DeptAccount[]>>(`${BASE_URL}/assignments/accounts`);
    return r.data.data;
  },
  /** Roles + dimensions for one account. Drives the whole screen — no hard-coded role lists. */
  async getConfig(acntId: number): Promise<AccountConfig> {
    const r = await axiosInstance.get<ApiResponse<AccountConfig>>(`${BASE_URL}/assignments/config/${acntId}`);
    return r.data.data;
  },
  async listPrincipals(params: { acnt_id: number; role_cd?: string; q?: string }): Promise<Principal[]> {
    const r = await axiosInstance.get<ApiResponse<Principal[]>>(`${BASE_URL}/assignments/principals`, { params });
    return r.data.data;
  },
  async getSummary(params?: Record<string, unknown>): Promise<AsgnmtSummary> {
    const r = await axiosInstance.get<ApiResponse<AsgnmtSummary>>(`${BASE_URL}/assignments/summary`, { params });
    return r.data.data;
  },
  async listPackages(params?: Record<string, unknown>): Promise<{ packages: AsgnmtPackage[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> {
    const r = await axiosInstance.get<ApiResponse<{ packages: AsgnmtPackage[]; pagination: any }>>(`${BASE_URL}/assignments/packages`, { params });
    return r.data.data;
  },
  async listPackageWorks(pkgeId: number): Promise<AsgnmtWork[]> {
    const r = await axiosInstance.get<ApiResponse<AsgnmtWork[]>>(`${BASE_URL}/assignments/packages/${pkgeId}/works`);
    return r.data.data;
  },
  /** Flat list of every work in scope — the "Works" tab of the assignment picker. */
  async listWorks(params?: Record<string, unknown>): Promise<{ works: AsgnmtWork[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> {
    const r = await axiosInstance.get<ApiResponse<{ works: AsgnmtWork[]; pagination: any }>>(`${BASE_URL}/assignments/works`, { params });
    return r.data.data;
  },
  /** Dry run. Same validator the commit uses, so a warning shown can never disagree with what commit does. */
  async preview(body: AssignBody): Promise<AsgnmtPreview> {
    const r = await axiosInstance.post<ApiResponse<AsgnmtPreview>>(`${BASE_URL}/assignments/preview`, body);
    return r.data.data;
  },
  async assign(body: AssignBody): Promise<{ assigned: number; reassigned: number; skipped: number }> {
    const r = await axiosInstance.post<ApiResponse<{ assigned: number; reassigned: number; skipped: number }>>(
      `${BASE_URL}/assignments/bulk`, { ...body, confirm: true });
    return r.data.data;
  },
  async revoke(asgnmtIds: number[], rsn_tx?: string): Promise<{ revoked: number }> {
    const r = await axiosInstance.post<ApiResponse<{ revoked: number }>>(`${BASE_URL}/assignments/revoke`, { asgnmt_ids: asgnmtIds, rsn_tx });
    return r.data.data;
  },
  async getHistory(params?: Record<string, unknown>): Promise<{ history: AsgnmtHistoryRow[]; pagination: { total: number } }> {
    const r = await axiosInstance.get<ApiResponse<{ history: AsgnmtHistoryRow[]; pagination: any }>>(`${BASE_URL}/assignments/history`, { params });
    return r.data.data;
  },
  /** "Which works can this login actually see, and why?" — the check that catches a bad rule before it ships. */
  async getEffectiveAccess(principalId: number, limit = 100): Promise<EffectiveAccess> {
    const r = await axiosInstance.get<ApiResponse<EffectiveAccess>>(`${BASE_URL}/assignments/effective/${principalId}`, { params: { limit } });
    return r.data.data;
  }
};



// Export default service object
const wmsService = {
  dashboard: dashboardApi,
  deptGroups: deptGroupsApi,
  works: worksApi,
  worksList: worksListApi,
  masters: mastersApi,
  proposals: proposalsApi,
  estimates: estimatesApi,
  designs: designsApi,
  technicalSanctions: technicalSanctionsApi,
  adminSanctions: adminSanctionsApi,
  workSchedule: workScheduleApi,
  delayEot: delayEotApi,
  tenders: tendersApi,
  workPackages: workPackagesApi,
  tenderSections: tenderSectionsApi,
  tenderExecution: tenderExecutionApi,
  awardAgreement: awardAgreementApi,
  contractors: contractorsApi,
  contracts: contractsApi,
  payments: paymentsApi,
  measurements: measurementsApi,
  checkMeasurements: checkMeasurementsApi,
  qc: qcApi,
  ncr: ncrApi,
  inspections: inspectionsApi,
  sanctions: sanctionsApi,
  workGis: workGisApi,
  attachments: attachmentApi
};

export default wmsService;

/**
 * Bills register (GET /bills) — read for the Financial tab's trend chart and bill
 * drill-downs. The server applies the caller's UDA scope; it does not take the
 * dashboard filter bar. Dates arrive as DD-MM-YYYY.
 */
export const billsApi = {
  async getAll(page = 1, limit = 1000, params: { status?: string; billType?: string; search?: string } = {}) {
    const response = await axiosInstance.get(`${BASE_URL}/bills`, { params: { ...params, page, limit } });
    return { data: response.data.data as any[], pagination: response.data.pagination };
  },
};
